import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState
} from "@xyflow/react"
import {
  Activity,
  Airplay,
  AlarmClock,
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  AtSign,
  Award,
  BadgeCheck,
  Banknote,
  BarChart3,
  Barcode,
  Bed,
  Bell,
  BookOpen,
  Bookmark,
  Bot,
  Box,
  Boxes,
  Briefcase,
  Bug,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarDays,
  Camera,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDollarSign,
  CircleHelp,
  CirclePlus,
  CircleX,
  ClipboardCheck,
  Clock3,
  Cloud,
  CloudUpload,
  Code2,
  Cog,
  Columns3,
  Compass,
  Copy,
  CreditCard,
  Cpu,
  Crown,
  Database,
  DollarSign,
  Download,
  Edit3,
  File,
  FileArchive,
  FileCheck,
  FileCode2,
  FileDown,
  FileSearch,
  FileText,
  FileUp,
  Files,
  Filter,
  Flag,
  Folder,
  FolderOpen,
  Gauge,
  GitBranch,
  Globe,
  Grid3X3,
  Handshake,
  HardDrive,
  Headphones,
  Heart,
  Home,
  Image,
  Inbox,
  Info,
  Key,
  Landmark,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Link,
  ListChecks,
  Lightbulb,
  LoaderCircle,
  Lock,
  LocateFixed,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquare,
  Monitor,
  MousePointerClick,
  Navigation,
  Navigation2,
  Package,
  PackageCheck,
  PanelTop,
  Paperclip,
  Percent,
  Pencil,
  Phone,
  PieChart,
  Play,
  Plus,
  Printer,
  Puzzle,
  QrCode,
  Receipt,
  Repeat,
  Rocket,
  Route,
  Save,
  ScanLine,
  Search as SearchIcon,
  SearchCheck,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Split,
  Smartphone,
  SquareCheck,
  SquarePen,
  Star,
  Store,
  Table,
  Tags,
  Target,
  Terminal,
  ThumbsUp,
  Ticket,
  Timer,
  TrendingUp,
  Trophy,
  Truck,
  Trash2,
  Upload,
  UserCheck,
  UserCog,
  UserPlus,
  UserRound,
  UsersRound,
  Video,
  Wallet,
  Webhook,
  Wifi,
  Workflow,
  Wrench,
  X,
  Zap,
  ZoomIn,
  ZoomOut
} from "lucide-react"
import { architectureThinkingLines } from "./ArchitectureConsole"
import { AgentActivityFeed, formatEventText } from "./AgentActivityFeed"
import { DetailShareButton } from "./DetailShareButton"
import { LoadingOrb } from "./LoadingOrb"
import { PullLatestButton } from "./PullLatestButton"
import { contextOverviewFromPayload, patchRepositoryContext } from "../lib/repositoryContext"
import { csrfToken } from "../lib/rails"
import { scrollPageToTop } from "../lib/scroll"
import { createSerializedLatestQueue } from "../lib/serializedLatestQueue"
import { useDismissiblePopover } from "../lib/useDismissiblePopover"

const minimumStoryboardFrameCount = 3
const maximumStoryboardFrameCount = 10
const contextActionDiagramHeightMessageType = "vibecheck:context-action-diagram:height"
const contextActionDiagramMeasureRequestType = "vibecheck:context-action-diagram:measure"
const maximumContextActionDiagramFrameHeight = 30000
const contextAvatarPollDelays = [ 15000, 30000, 60000, 120000 ]
const maximumContextAvatarPollAttempts = 10
const maximumActionPersonaAvatars = 4
const activeStoryboardGenerationStatuses = new Set([ "pending", "generating" ])
const contextSemanticSignatureIgnoredKeys = new Set([
  "avatar",
  "position",
  "positionX",
  "positionY",
  "position_x",
  "position_y",
  "x",
  "y"
])

const commonIconOptions = [
  { key: "search", label: "Search", icon: SearchIcon, keywords: [ "find", "lookup", "filter" ] },
  { key: "github", label: "GitHub", icon: GitBranch, keywords: [ "git", "repository", "code", "branch" ] },
  { key: "settings", label: "Settings", icon: Settings, keywords: [ "configure", "preferences", "admin" ] },
  { key: "user", label: "User", icon: UserRound, keywords: [ "person", "profile", "customer" ] },
  { key: "users", label: "Users", icon: UsersRound, keywords: [ "team", "collaborators", "people" ] },
  { key: "home", label: "Home", icon: Home, keywords: [ "house", "property", "dashboard" ] },
  { key: "building", label: "Building", icon: Building2, keywords: [ "company", "office", "organization" ] },
  { key: "calendar", label: "Calendar", icon: CalendarDays, keywords: [ "schedule", "appointment", "date" ] },
  { key: "clock", label: "Clock", icon: Clock3, keywords: [ "time", "history", "recent" ] },
  { key: "check", label: "Check", icon: CheckCircle2, keywords: [ "complete", "done", "success" ] },
  { key: "alert", label: "Alert", icon: AlertTriangle, keywords: [ "warning", "issue", "risk" ] },
  { key: "bell", label: "Bell", icon: Bell, keywords: [ "notification", "reminder", "updates" ] },
  { key: "mail", label: "Mail", icon: Mail, keywords: [ "email", "message", "inbox" ] },
  { key: "phone", label: "Phone", icon: Phone, keywords: [ "call", "contact", "support" ] },
  { key: "map", label: "Map pin", icon: MapPin, keywords: [ "location", "address", "route" ] },
  { key: "document", label: "Document", icon: FileText, keywords: [ "file", "record", "page" ] },
  { key: "link", label: "Link", icon: Link, keywords: [ "url", "connection", "reference" ] },
  { key: "clipboard", label: "Clipboard", icon: ClipboardCheck, keywords: [ "task", "checklist", "work order" ] },
  { key: "list", label: "List", icon: ListChecks, keywords: [ "tasks", "queue", "items" ] },
  { key: "chart", label: "Chart", icon: BarChart3, keywords: [ "analytics", "report", "metrics" ] },
  { key: "database", label: "Database", icon: Database, keywords: [ "data", "storage", "records" ] },
  { key: "dollar", label: "Dollar", icon: DollarSign, keywords: [ "money", "price", "revenue" ] },
  { key: "credit", label: "Credit card", icon: CreditCard, keywords: [ "payment", "billing", "card" ] },
  { key: "shop", label: "Shopping cart", icon: ShoppingCart, keywords: [ "order", "checkout", "purchase" ] },
  { key: "upload", label: "Upload", icon: Upload, keywords: [ "import", "attach", "send file" ] },
  { key: "download", label: "Download", icon: Download, keywords: [ "export", "save", "file" ] },
  { key: "send", label: "Send", icon: Send, keywords: [ "submit", "message", "deliver" ] },
  { key: "edit", label: "Edit", icon: Edit3, keywords: [ "write", "update", "change" ] },
  { key: "trash", label: "Trash", icon: Trash2, keywords: [ "delete", "remove", "discard" ] },
  { key: "lock", label: "Lock", icon: Lock, keywords: [ "security", "permissions", "private" ] },
  { key: "archive", label: "Archive", icon: Archive, keywords: [ "store", "close", "hide" ] },
  { key: "truck", label: "Truck", icon: Truck, keywords: [ "dispatch", "delivery", "field service" ] },
  { key: "wrench", label: "Wrench", icon: Wrench, keywords: [ "repair", "service", "maintenance" ] },
  { key: "activity", label: "Activity", icon: Activity, keywords: [ "pulse", "heartbeat", "monitoring" ] },
  { key: "airplay", label: "Airplay", icon: Airplay, keywords: [ "screen", "cast", "display" ] },
  { key: "alarm", label: "Alarm", icon: AlarmClock, keywords: [ "reminder", "deadline", "timer" ] },
  { key: "arrow-down", label: "Arrow down", icon: ArrowDown, keywords: [ "down", "decrease", "next" ] },
  { key: "arrow-left", label: "Arrow left", icon: ArrowLeft, keywords: [ "back", "previous", "return" ] },
  { key: "arrow-right", label: "Arrow right", icon: ArrowRight, keywords: [ "next", "forward", "continue" ] },
  { key: "arrow-up", label: "Arrow up", icon: ArrowUp, keywords: [ "up", "increase", "promote" ] },
  { key: "at-sign", label: "At sign", icon: AtSign, keywords: [ "mention", "email", "handle" ] },
  { key: "award", label: "Award", icon: Award, keywords: [ "achievement", "quality", "badge" ] },
  { key: "badge-check", label: "Badge check", icon: BadgeCheck, keywords: [ "verified", "approved", "trusted" ] },
  { key: "banknote", label: "Banknote", icon: Banknote, keywords: [ "cash", "payment", "finance" ] },
  { key: "barcode", label: "Barcode", icon: Barcode, keywords: [ "scan", "sku", "inventory" ] },
  { key: "bed", label: "Bed", icon: Bed, keywords: [ "room", "lodging", "home service" ] },
  { key: "book", label: "Book", icon: BookOpen, keywords: [ "docs", "guide", "manual" ] },
  { key: "bookmark", label: "Bookmark", icon: Bookmark, keywords: [ "save", "favorite", "mark" ] },
  { key: "bot", label: "Bot", icon: Bot, keywords: [ "ai", "automation", "assistant" ] },
  { key: "box", label: "Box", icon: Box, keywords: [ "package", "inventory", "container" ] },
  { key: "boxes", label: "Boxes", icon: Boxes, keywords: [ "warehouse", "inventory", "items" ] },
  { key: "briefcase", label: "Briefcase", icon: Briefcase, keywords: [ "business", "work", "portfolio" ] },
  { key: "bug", label: "Bug", icon: Bug, keywords: [ "error", "defect", "issue" ] },
  { key: "calculator", label: "Calculator", icon: Calculator, keywords: [ "estimate", "quote", "pricing" ] },
  { key: "calendar-check", label: "Calendar check", icon: CalendarCheck, keywords: [ "scheduled", "booked", "appointment" ] },
  { key: "camera", label: "Camera", icon: Camera, keywords: [ "photo", "image", "capture" ] },
  { key: "car", label: "Car", icon: Car, keywords: [ "vehicle", "travel", "fleet" ] },
  { key: "chevron-down", label: "Chevron down", icon: ChevronDown, keywords: [ "expand", "open", "dropdown" ] },
  { key: "chevron-right", label: "Chevron right", icon: ChevronRight, keywords: [ "next", "open", "details" ] },
  { key: "circle-check", label: "Circle check", icon: CircleCheck, keywords: [ "done", "approved", "complete" ] },
  { key: "circle-dollar", label: "Circle dollar", icon: CircleDollarSign, keywords: [ "money", "price", "payment" ] },
  { key: "circle-help", label: "Circle help", icon: CircleHelp, keywords: [ "help", "question", "support" ] },
  { key: "circle-plus", label: "Circle plus", icon: CirclePlus, keywords: [ "add", "create", "new" ] },
  { key: "circle-x", label: "Circle x", icon: CircleX, keywords: [ "cancel", "close", "remove" ] },
  { key: "cloud", label: "Cloud", icon: Cloud, keywords: [ "online", "hosting", "sync" ] },
  { key: "cloud-upload", label: "Cloud upload", icon: CloudUpload, keywords: [ "sync", "backup", "upload" ] },
  { key: "code", label: "Code", icon: Code2, keywords: [ "developer", "programming", "source" ] },
  { key: "cog", label: "Cog", icon: Cog, keywords: [ "settings", "configuration", "gear" ] },
  { key: "columns", label: "Columns", icon: Columns3, keywords: [ "layout", "board", "kanban" ] },
  { key: "compass", label: "Compass", icon: Compass, keywords: [ "discover", "navigate", "direction" ] },
  { key: "copy", label: "Copy", icon: Copy, keywords: [ "duplicate", "clone", "clipboard" ] },
  { key: "cpu", label: "CPU", icon: Cpu, keywords: [ "system", "hardware", "processing" ] },
  { key: "crown", label: "Crown", icon: Crown, keywords: [ "owner", "admin", "premium" ] },
  { key: "file", label: "File", icon: File, keywords: [ "document", "attachment", "paper" ] },
  { key: "file-archive", label: "File archive", icon: FileArchive, keywords: [ "zip", "stored", "backup" ] },
  { key: "file-check", label: "File check", icon: FileCheck, keywords: [ "approved", "signed", "verified" ] },
  { key: "file-code", label: "File code", icon: FileCode2, keywords: [ "source", "programming", "script" ] },
  { key: "file-down", label: "File download", icon: FileDown, keywords: [ "download", "export", "save" ] },
  { key: "file-search", label: "File search", icon: FileSearch, keywords: [ "inspect", "review", "find" ] },
  { key: "file-up", label: "File upload", icon: FileUp, keywords: [ "upload", "import", "attach" ] },
  { key: "files", label: "Files", icon: Files, keywords: [ "documents", "attachments", "records" ] },
  { key: "filter", label: "Filter", icon: Filter, keywords: [ "funnel", "refine", "search" ] },
  { key: "flag", label: "Flag", icon: Flag, keywords: [ "mark", "priority", "milestone" ] },
  { key: "folder", label: "Folder", icon: Folder, keywords: [ "directory", "project", "collection" ] },
  { key: "folder-open", label: "Folder open", icon: FolderOpen, keywords: [ "open", "directory", "project" ] },
  { key: "gauge", label: "Gauge", icon: Gauge, keywords: [ "status", "performance", "speed" ] },
  { key: "globe", label: "Globe", icon: Globe, keywords: [ "web", "website", "global" ] },
  { key: "grid", label: "Grid", icon: Grid3X3, keywords: [ "apps", "tiles", "layout" ] },
  { key: "handshake", label: "Handshake", icon: Handshake, keywords: [ "agreement", "partner", "deal" ] },
  { key: "hard-drive", label: "Hard drive", icon: HardDrive, keywords: [ "storage", "disk", "backup" ] },
  { key: "headphones", label: "Headphones", icon: Headphones, keywords: [ "support", "service", "helpdesk" ] },
  { key: "heart", label: "Heart", icon: Heart, keywords: [ "favorite", "health", "care" ] },
  { key: "image", label: "Image", icon: Image, keywords: [ "photo", "media", "picture" ] },
  { key: "inbox", label: "Inbox", icon: Inbox, keywords: [ "messages", "queue", "issues" ] },
  { key: "info", label: "Info", icon: Info, keywords: [ "details", "help", "about" ] },
  { key: "key", label: "Key", icon: Key, keywords: [ "access", "password", "credential" ] },
  { key: "landmark", label: "Landmark", icon: Landmark, keywords: [ "bank", "institution", "government" ] },
  { key: "layers", label: "Layers", icon: Layers, keywords: [ "stack", "levels", "group" ] },
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, keywords: [ "overview", "metrics", "home" ] },
  { key: "life-buoy", label: "Life buoy", icon: LifeBuoy, keywords: [ "support", "rescue", "help" ] },
  { key: "lightbulb", label: "Lightbulb", icon: Lightbulb, keywords: [ "idea", "insight", "tip" ] },
  { key: "loader", label: "Loader", icon: LoaderCircle, keywords: [ "loading", "progress", "spinner" ] },
  { key: "locate", label: "Locate", icon: LocateFixed, keywords: [ "gps", "target", "position" ] },
  { key: "log-in", label: "Log in", icon: LogIn, keywords: [ "signin", "enter", "access" ] },
  { key: "log-out", label: "Log out", icon: LogOut, keywords: [ "signout", "exit", "leave" ] },
  { key: "megaphone", label: "Megaphone", icon: Megaphone, keywords: [ "announcement", "marketing", "broadcast" ] },
  { key: "menu", label: "Menu", icon: Menu, keywords: [ "navigation", "hamburger", "list" ] },
  { key: "message-circle", label: "Message circle", icon: MessageCircle, keywords: [ "chat", "comment", "conversation" ] },
  { key: "message", label: "Message", icon: MessageSquare, keywords: [ "chat", "comment", "conversation" ] },
  { key: "monitor", label: "Monitor", icon: Monitor, keywords: [ "screen", "desktop", "display" ] },
  { key: "mouse-click", label: "Mouse click", icon: MousePointerClick, keywords: [ "select", "click", "pointer" ] },
  { key: "navigation", label: "Navigation", icon: Navigation, keywords: [ "direction", "route", "go" ] },
  { key: "navigation-2", label: "Navigation 2", icon: Navigation2, keywords: [ "direction", "route", "location" ] },
  { key: "package", label: "Package", icon: Package, keywords: [ "box", "shipment", "product" ] },
  { key: "package-check", label: "Package check", icon: PackageCheck, keywords: [ "delivered", "verified", "inventory" ] },
  { key: "panel", label: "Panel", icon: PanelTop, keywords: [ "layout", "window", "topbar" ] },
  { key: "paperclip", label: "Paperclip", icon: Paperclip, keywords: [ "attachment", "file", "upload" ] },
  { key: "percent", label: "Percent", icon: Percent, keywords: [ "discount", "rate", "ratio" ] },
  { key: "pie-chart", label: "Pie chart", icon: PieChart, keywords: [ "analytics", "report", "segment" ] },
  { key: "play", label: "Play", icon: Play, keywords: [ "start", "run", "video" ] },
  { key: "printer", label: "Printer", icon: Printer, keywords: [ "print", "paper", "document" ] },
  { key: "puzzle", label: "Puzzle", icon: Puzzle, keywords: [ "plugin", "integration", "extension" ] },
  { key: "qr-code", label: "QR code", icon: QrCode, keywords: [ "scan", "code", "mobile" ] },
  { key: "receipt", label: "Receipt", icon: Receipt, keywords: [ "invoice", "billing", "purchase" ] },
  { key: "repeat", label: "Repeat", icon: Repeat, keywords: [ "recurring", "refresh", "again" ] },
  { key: "rocket", label: "Rocket", icon: Rocket, keywords: [ "launch", "ship", "deploy" ] },
  { key: "route", label: "Route", icon: Route, keywords: [ "path", "trip", "dispatch" ] },
  { key: "save", label: "Save", icon: Save, keywords: [ "store", "persist", "disk" ] },
  { key: "scan", label: "Scan", icon: ScanLine, keywords: [ "scanner", "barcode", "capture" ] },
  { key: "search-check", label: "Search check", icon: SearchCheck, keywords: [ "verify", "inspect", "audit" ] },
  { key: "server", label: "Server", icon: Server, keywords: [ "backend", "hosting", "infrastructure" ] },
  { key: "share", label: "Share", icon: Share2, keywords: [ "send", "collaborate", "social" ] },
  { key: "shield", label: "Shield", icon: Shield, keywords: [ "security", "safety", "protection" ] },
  { key: "shield-check", label: "Shield check", icon: ShieldCheck, keywords: [ "verified", "secure", "protected" ] },
  { key: "sliders", label: "Sliders", icon: SlidersHorizontal, keywords: [ "controls", "settings", "filters" ] },
  { key: "smartphone", label: "Smartphone", icon: Smartphone, keywords: [ "mobile", "phone", "device" ] },
  { key: "split", label: "Split", icon: Split, keywords: [ "branch", "divide", "workflow" ] },
  { key: "square-check", label: "Square check", icon: SquareCheck, keywords: [ "checkbox", "done", "complete" ] },
  { key: "square-pen", label: "Square pen", icon: SquarePen, keywords: [ "edit", "compose", "write" ] },
  { key: "star", label: "Star", icon: Star, keywords: [ "favorite", "rating", "quality" ] },
  { key: "store", label: "Store", icon: Store, keywords: [ "shop", "retail", "business" ] },
  { key: "table", label: "Table", icon: Table, keywords: [ "spreadsheet", "rows", "data" ] },
  { key: "tags", label: "Tags", icon: Tags, keywords: [ "labels", "metadata", "categories" ] },
  { key: "target", label: "Target", icon: Target, keywords: [ "goal", "focus", "objective" ] },
  { key: "terminal", label: "Terminal", icon: Terminal, keywords: [ "command", "cli", "developer" ] },
  { key: "thumbs-up", label: "Thumbs up", icon: ThumbsUp, keywords: [ "approve", "like", "positive" ] },
  { key: "ticket", label: "Ticket", icon: Ticket, keywords: [ "support", "issue", "event" ] },
  { key: "timer", label: "Timer", icon: Timer, keywords: [ "duration", "countdown", "time" ] },
  { key: "trending-up", label: "Trending up", icon: TrendingUp, keywords: [ "growth", "increase", "analytics" ] },
  { key: "trophy", label: "Trophy", icon: Trophy, keywords: [ "win", "award", "success" ] },
  { key: "user-check", label: "User check", icon: UserCheck, keywords: [ "verified user", "approved", "member" ] },
  { key: "user-cog", label: "User cog", icon: UserCog, keywords: [ "admin", "account settings", "role" ] },
  { key: "user-plus", label: "User plus", icon: UserPlus, keywords: [ "invite", "add user", "member" ] },
  { key: "video", label: "Video", icon: Video, keywords: [ "recording", "media", "camera" ] },
  { key: "wallet", label: "Wallet", icon: Wallet, keywords: [ "payment", "money", "account" ] },
  { key: "webhook", label: "Webhook", icon: Webhook, keywords: [ "integration", "automation", "api" ] },
  { key: "wifi", label: "Wifi", icon: Wifi, keywords: [ "network", "connection", "wireless" ] },
  { key: "workflow", label: "Workflow", icon: Workflow, keywords: [ "automation", "process", "flow" ] },
  { key: "zap", label: "Zap", icon: Zap, keywords: [ "fast", "automation", "energy" ] },
  { key: "zoom-in", label: "Zoom in", icon: ZoomIn, keywords: [ "magnify", "expand", "inspect" ] },
  { key: "zoom-out", label: "Zoom out", icon: ZoomOut, keywords: [ "minimize", "reduce", "view" ] }
].sort(compareIconOptions)

const iconMap = {
  ...Object.fromEntries(commonIconOptions.map((option) => [ option.key, option.icon ])),
  appointment: CalendarDays,
  branch: GitBranch,
  chart: BarChart3,
  communicate: MessageSquare,
  customer: UserRound,
  dispatch: Truck,
  goal: Target,
  payment: CreditCard,
  schedule: Clock3,
  service: Package,
  status: Gauge,
  success: CheckCircle2
}

const nodePalette = [
  "violet",
  "green",
  "amber",
  "blue",
  "rose",
  "magenta",
  "cyan"
]
const graphDefaults = {
  height: 520,
  minColumnGap: 320,
  minRowGap: 184,
  nodeHeight: 116,
  nodeWidth: 220,
  width: 1120
}
const contextGraphLayoutPadding = 72
const contextGraphReadableGap = 32
export const contextFlowMinimumZoom = 0.05
export const relationshipFlowFitViewOptions = { maxZoom: 1, minZoom: contextFlowMinimumZoom, padding: 0.16 }
const contextFlowNodeOrigin = [0.5, 0.5]
const contextFlowProOptions = { hideAttribution: true }
export const contextFlowDeletionProtection = {
  deleteKeyCode: null,
  onBeforeDelete: preventLocalGraphDeletion
}

export function ContextOverview({
  avatarRegenerationEnabled = false,
  avatarRegenerationPath,
  context,
  contextStatusPath,
  diagramPaths,
  diagramRegenerationEnabled = false,
  editable = false,
  generating = false,
  initialDiagrams = [],
  initialSelectedActionKey = null,
  initialSelectedPersonaKey = null,
  initialStoryboards = [],
  issues = [],
  concept = null,
  onActionDetailChange,
  onContextUpdate,
  onEmphasisTargetChange = null,
  onSelectedActionKeyChange,
  onSelectedPersonaKeyChange,
  selectedActionKey: controlledSelectedActionKey,
  selectedPersonaKey: controlledSelectedPersonaKey,
  storyboardPaths,
  updatePath,
  streamLines = []
}) {
  const [editingItem, setEditingItem] = useState(null)
  const [saveState, setSaveState] = useState("idle")
  const [saveError, setSaveError] = useState(null)
  const latestMovePayloadRef = useRef(null)
  const applyContextUpdatePayloadRef = useRef(null)
  const moveSaveQueueRef = useRef(null)
  const pendingStoryboardActionKeysRef = useRef(new Set())
  const pendingDiagramActionKeysRef = useRef(new Set())
  const queuedStoryboardActionKeysRef = useRef(new Set())
  const storyboardContextSignatureRef = useRef(null)
  const [diagrams, setDiagrams] = useState(initialDiagrams)
  const [diagramBusy, setDiagramBusy] = useState(false)
  const [diagramError, setDiagramError] = useState(null)
  const [storyboards, setStoryboards] = useState(initialStoryboards)
  const [storyboardBusy, setStoryboardBusy] = useState(false)
  const [storyboardError, setStoryboardError] = useState(null)
  const [editingStoryboardFrame, setEditingStoryboardFrame] = useState(null)
  const [storyboardFrameSaveState, setStoryboardFrameSaveState] = useState("idle")
  const [storyboardFrameSaveError, setStoryboardFrameSaveError] = useState(null)
  const [regeneratingStoryboardFrameKeys, setRegeneratingStoryboardFrameKeys] = useState(new Set())
  const [avatarRegenerationBusy, setAvatarRegenerationBusy] = useState(false)
  const [avatarRegenerationError, setAvatarRegenerationError] = useState(null)
  const selectedActionKeyControlled = controlledSelectedActionKey !== undefined
  const selectedPersonaKeyControlled = controlledSelectedPersonaKey !== undefined
  const [localSelectedActionKey, setLocalSelectedActionKey] = useState(initialSelectedActionKey)
  const [localSelectedPersonaKey, setLocalSelectedPersonaKey] = useState(initialSelectedPersonaKey)
  const selectedActionKey = selectedActionKeyControlled ? controlledSelectedActionKey : localSelectedActionKey
  const selectedPersonaKey = selectedPersonaKeyControlled ? controlledSelectedPersonaKey : localSelectedPersonaKey
  const canEdit = Boolean(editable && updatePath && context && !generating)
  const storyboardContextSignature = useMemo(() => storyboardContextSignatureFor(context), [context])
  const storyboardsGenerating = storyboards.some(storyboardGenerationActive)
  const regeneratingStoryboardFrameCount = regeneratingStoryboardFrameKeys.size
  const storyboardsByKey = useMemo(() => {
    return new Map(storyboards.map((storyboard) => [storyboard.actionKey, storyboard]))
  }, [storyboards])
  const actionItems = limitedList(context?.actions)
  const allActionStoryboardsReady = actionStoryboardsReady(actionItems, storyboardsByKey)
  const diagramItems = diagramList(diagrams)
  const diagramsByKey = useMemo(() => diagramsByActionKey(diagramItems), [diagrams])
  const diagramsGenerating = diagramItems.some((diagram) => diagram.status === "generating")
  const generatingAvatarSignature = useMemo(() => contextGeneratingAvatarSignature(context), [context])
  const avatarGenerationInProgress = Boolean(generatingAvatarSignature)
  const storyboardGenerationInProgress = storyboardBusy || storyboardsGenerating
  const selectedAction = useMemo(() => {
    if (!selectedActionKey) return null

    return limitedList(context?.actions).find((action) => contextActionKey(action) === selectedActionKey) || null
  }, [context?.actions, selectedActionKey])
  const selectedPersona = useMemo(() => {
    if (!selectedPersonaKey) return null

    return limitedList(context?.users).find((user) => contextPersonaKey(user) === selectedPersonaKey) || null
  }, [context?.users, selectedPersonaKey])
  const detailOpen = Boolean(selectedAction || selectedPersona)
  const updateSelectedActionKey = useCallback((nextActionKey) => {
    if (!selectedActionKeyControlled) setLocalSelectedActionKey(nextActionKey)
    onSelectedActionKeyChange?.(nextActionKey)
  }, [onSelectedActionKeyChange, selectedActionKeyControlled])
  const updateSelectedPersonaKey = useCallback((nextPersonaKey) => {
    if (!selectedPersonaKeyControlled) setLocalSelectedPersonaKey(nextPersonaKey)
    onSelectedPersonaKeyChange?.(nextPersonaKey)
  }, [onSelectedPersonaKeyChange, selectedPersonaKeyControlled])

  useEffect(() => {
    onActionDetailChange?.(detailOpen)
  }, [detailOpen, onActionDetailChange])

  useEffect(() => {
    return () => onActionDetailChange?.(false)
  }, [onActionDetailChange])

  useEffect(() => {
    if (!pendingStoryboardActionKeysRef.current.size) return

    const generatingActionKeys = new Set(
      storyboards
        .filter(storyboardGenerationActive)
        .map((storyboard) => storyboard.actionKey)
    )

    pendingStoryboardActionKeysRef.current.forEach((actionKey) => {
      if (!generatingActionKeys.has(actionKey)) pendingStoryboardActionKeysRef.current.delete(actionKey)
    })
  }, [storyboards])

  useEffect(() => {
    if (!pendingDiagramActionKeysRef.current.size) return

    const generatingActionKeys = new Set(
      diagrams
        .filter((diagram) => diagram.status === "generating")
        .map((diagram) => diagram.actionKey)
    )

    pendingDiagramActionKeysRef.current.forEach((actionKey) => {
      if (!generatingActionKeys.has(actionKey)) pendingDiagramActionKeysRef.current.delete(actionKey)
    })
  }, [diagrams])

  useEffect(() => {
    setRegeneratingStoryboardFrameKeys((currentKeys) => {
      if (!currentKeys.size) return currentKeys

      const nextKeys = new Set(currentKeys)
      currentKeys.forEach((key) => {
        const actionKey = storyboardFrameRegenerationActionKey(key)
        const storyboard = storyboardsByKey.get(actionKey)
        if (!storyboardGenerationActive(storyboard)) nextKeys.delete(key)
      })

      return nextKeys.size === currentKeys.size ? currentKeys : nextKeys
    })
  }, [storyboardsByKey])

  useEffect(() => {
    setStoryboards((currentStoryboards) => {
      return mergeStoryboardsWithPendingGeneration(
        initialStoryboards || [],
        currentStoryboards,
        pendingStoryboardActionKeysRef.current
      )
    })
  }, [initialStoryboards])

  useEffect(() => {
    setDiagrams(initialDiagrams || [])
  }, [initialDiagrams])

  useEffect(() => {
    if (storyboardContextSignatureRef.current === null) {
      storyboardContextSignatureRef.current = storyboardContextSignature
      return
    }

    if (storyboardContextSignatureRef.current === storyboardContextSignature) return

    storyboardContextSignatureRef.current = storyboardContextSignature
    pendingStoryboardActionKeysRef.current.clear()
    pendingDiagramActionKeysRef.current.clear()
    setRegeneratingStoryboardFrameKeys((currentKeys) => currentKeys.size ? new Set() : currentKeys)
    setEditingStoryboardFrame(null)
    setStoryboardFrameSaveError(null)
    setDiagramError(null)
    setDiagrams([])
    setStoryboards([])
  }, [storyboardContextSignature])

  useEffect(() => {
    if (!diagramPaths?.index || !context?.actions?.length) return undefined

    let cancelled = false

    fetchDiagrams({ cancelled: () => cancelled })

    return () => {
      cancelled = true
    }
  }, [diagramPaths?.index, storyboardContextSignature])

  useEffect(() => {
    if (!diagramPaths?.index || !diagramsGenerating) return undefined

    let cancelled = false
    const interval = window.setInterval(() => {
      fetchDiagrams({ cancelled: () => cancelled })
    }, 5000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [diagramPaths?.index, diagramsGenerating])

  useEffect(() => {
    if (!storyboardPaths?.index || !context?.actions?.length) {
      setStoryboards([])
      return undefined
    }

    let cancelled = false

    fetchStoryboards({ cancelled: () => cancelled })

    return () => {
      cancelled = true
    }
  }, [storyboardPaths?.index, storyboardContextSignature])

  useEffect(() => {
    if (!storyboardsGenerating || !storyboardPaths?.index) return undefined

    let cancelled = false
    const interval = window.setInterval(() => {
      fetchStoryboards({ cancelled: () => cancelled })
    }, regeneratingStoryboardFrameCount ? 1000 : 5000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [regeneratingStoryboardFrameCount, storyboardsGenerating, storyboardPaths?.index])

  useEffect(() => {
    if (!contextStatusPath || !generatingAvatarSignature) return undefined

    let cancelled = false
    let shouldContinuePolling = true
    let timeout = null
    let attempt = 0

    const scheduleNextPoll = () => {
      if (cancelled || !shouldContinuePolling || attempt >= maximumContextAvatarPollAttempts) return

      timeout = window.setTimeout(pollAvatars, contextAvatarPollDelay(attempt))
      attempt += 1
    }

    const pollAvatars = () => {
      timeout = null

      return fetch(contextStatusPath, {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Cache-Control": "no-store"
        }
      })
        .then((response) => {
          if (!response.ok) throw new Error("Could not refresh persona avatars.")
          return response.json()
        })
        .then((payload) => {
          if (cancelled) return
          const nextContext = contextOverviewFromPayload(payload)
          if (!nextContext) return

          shouldContinuePolling = contextHasGeneratingAvatars(nextContext)
          return preloadContextAvatarImages(nextContext).then(() => {
            if (!cancelled) onContextUpdate?.(nextContext)
          })
        })
        .catch(() => {})
        .finally(scheduleNextPoll)
    }

    scheduleNextPoll()

    return () => {
      cancelled = true
      if (timeout) window.clearTimeout(timeout)
    }
  }, [contextStatusPath, generatingAvatarSignature, onContextUpdate])

  useEffect(() => {
    if (!queuedStoryboardActionKeysRef.current.size) return
    if (storyboardGenerationInProgress || !storyboardPaths?.create) return

    const currentActionKeys = new Set(limitedList(context?.actions).map((action) => contextActionKey(action)))
    queuedStoryboardActionKeysRef.current.forEach((actionKey) => {
      if (!currentActionKeys.has(actionKey)) queuedStoryboardActionKeysRef.current.delete(actionKey)
    })
    const nextActionKey = queuedStoryboardActionKeysRef.current.values().next().value

    if (nextActionKey) queuedStoryboardActionKeysRef.current.delete(nextActionKey)
    if (nextActionKey) generateStoryboards({ actionKey: nextActionKey })
  }, [
    context?.actions,
    storyboardContextSignature,
    storyboardGenerationInProgress,
    storyboardPaths?.create
  ])

  useEffect(() => {
    if (!selectedActionKey) return
    if (limitedList(context?.actions).some((action) => contextActionKey(action) === selectedActionKey)) return

    updateSelectedActionKey(null)
  }, [context?.actions, selectedActionKey, updateSelectedActionKey])

  useEffect(() => {
    if (!selectedPersonaKey) return
    if (limitedList(context?.users).some((user) => contextPersonaKey(user) === selectedPersonaKey)) return

    updateSelectedPersonaKey(null)
  }, [context?.users, selectedPersonaKey, updateSelectedPersonaKey])

  useEffect(() => {
    scrollPageToTop()
  }, [selectedActionKey, selectedPersonaKey])

  if (!moveSaveQueueRef.current) {
    moveSaveQueueRef.current = createSerializedLatestQueue({
      run: ({ attributes, fallbackError, key, section, updatePath }) => patchRepositoryContext(updatePath, {
        section,
        key,
        attributes
      }, fallbackError),
      onSuccess: (payload) => {
        latestMovePayloadRef.current = payload
        setSaveError(null)
      },
      onError: (error) => {
        setSaveError(error?.message || "Could not save graph position.")
      },
      onIdle: () => {
        const payload = latestMovePayloadRef.current
        if (!payload) return

        latestMovePayloadRef.current = null
        applyContextUpdatePayloadRef.current?.(payload)
      }
    })
  }

  function openEditor(item) {
    setEditingItem(item)
    setSaveState("idle")
    setSaveError(null)
  }

  function openNewUser() {
    openEditor({ section: "users", title: "Add user group", item: {}, creating: true })
  }

  function openNewAction() {
    openEditor({ section: "actions", title: "Add user action", item: {}, creating: true })
  }

  function openNewSystemObject() {
    openEditor({ section: "system_objects", title: "Add key object", item: {}, creating: true })
  }

  function openNewSystemRelationship() {
    const objects = relationshipObjects(context?.system?.objects)
    if (objects.length < 2) return

    openEditor({
      section: "system_relationships",
      title: "Add relationship",
      item: {
        from: objects[0].id,
        label: "",
        to: objects[1].id
      },
      creating: true
    })
  }

  function openStoryboardFrameEditor(action, storyboard, frameIndex, { displayFrameIndex = frameIndex } = {}) {
    if (!canEdit || !storyboardFrameEditingAvailable(storyboard)) return

    const framePlan = storyboard.framePlan || []
    const frameIndexes = storyboardFrameIndexes(storyboard, storyboard.frames || [])
    const frame = framePlan[frameIndex] || {}

    setEditingStoryboardFrame({
      actionKey: contextActionKey(action),
      actionLabel: action.label,
      frame,
      frameCount: frameIndexes.length,
      frameIndex,
      title: `${action.label} frame ${displayFrameIndex + 1}`,
      updatePath: storyboard.updatePath
    })
    setStoryboardFrameSaveState("idle")
    setStoryboardFrameSaveError(null)
  }

  function fetchStoryboards({ cancelled = () => false } = {}) {
    fetch(storyboardPaths.index, {
      headers: { Accept: "application/json" }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load storyboards.")
        return response.json()
      })
      .then((payload) => {
        if (!cancelled()) {
          const nextStoryboards = Array.isArray(payload.storyboards) ? payload.storyboards : []
          setStoryboards((currentStoryboards) => {
            return mergeStoryboardsWithPendingGeneration(
              nextStoryboards,
              currentStoryboards,
              pendingStoryboardActionKeysRef.current
            )
          })
          setStoryboardError(null)
        }
      })
      .catch((error) => {
        if (!cancelled()) setStoryboardError(error.message)
      })
  }

  function fetchDiagrams({ cancelled = () => false } = {}) {
    fetch(diagramPaths.index, {
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store"
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load workflow diagrams.")
        return response.json()
      })
      .then((payload) => {
        if (!cancelled()) {
          setDiagrams(Array.isArray(payload.diagrams) ? payload.diagrams : [])
          setDiagramError(null)
        }
      })
      .catch(() => {
        if (!cancelled()) {
          setDiagrams([])
        }
      })
  }

  function generateDiagram({ actionKey, diagramType = "workflow" } = {}) {
    if (!diagramPaths?.create) return

    const targetAction = limitedList(context?.actions).find((action) => contextActionKey(action) === actionKey)
    if (!targetAction) return

    const targetDiagramType = diagramType || "workflow"
    const previousDiagrams = diagrams
    pendingDiagramActionKeysRef.current.add(actionKey)
    setDiagramBusy(true)
    setDiagramError(null)
    optimisticallyMarkDiagramGenerating(targetAction, { diagramType: targetDiagramType })

    const createPath = pathWithQueryParam(
      pathWithQueryParam(diagramPaths.create, "action_key", actionKey),
      "diagram_type",
      targetDiagramType
    )

    fetch(createPath, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken()
      }
    })
      .then((response) => response.json().then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (!response.ok) throw new Error(payload.error || "Could not generate the Archify diagram.")

        setDiagrams(Array.isArray(payload.diagrams) ? payload.diagrams : [])
      })
      .catch((error) => {
        pendingDiagramActionKeysRef.current.delete(actionKey)
        setDiagrams(previousDiagrams)
        setDiagramError(error.message || "Could not generate the Archify diagram.")
      })
      .finally(() => setDiagramBusy(false))
  }

  function optimisticallyMarkDiagramGenerating(action, { diagramType = "workflow" } = {}) {
    const actionKey = contextActionKey(action)
    if (!actionKey) return

    const targetDiagramType = diagramType || "workflow"
    const targetDiagramLabel = diagramTypeLabel({ diagramType: targetDiagramType })

    setDiagrams((currentDiagrams) => {
      const nextDiagrams = diagramList(currentDiagrams).filter((diagram) => {
        return !(diagram.actionKey === actionKey && diagram.diagramType === targetDiagramType)
      })

      nextDiagrams.push({
        actionKey,
        actionLabel: action.label || action.name,
        actionDescription: action.description,
        diagramType: targetDiagramType,
        label: targetDiagramLabel,
        title: `${action.label || action.name} ${targetDiagramLabel}`,
        status: "generating"
      })

      return nextDiagrams
    })
  }

  function mergeUpdatedDiagram(updatedDiagram) {
    if (!updatedDiagram?.actionKey) return

    setDiagrams((currentDiagrams) => {
      let replaced = false
      const nextDiagrams = diagramList(currentDiagrams).map((diagram) => {
        const sameId = updatedDiagram.id && diagram.id === updatedDiagram.id
        const sameActionDiagram = diagram.actionKey === updatedDiagram.actionKey &&
          diagram.diagramType === updatedDiagram.diagramType

        if (!sameId && !sameActionDiagram) return diagram

        replaced = true
        return updatedDiagram
      })

      return replaced ? nextDiagrams : [ ...nextDiagrams, updatedDiagram ]
    })
  }

  function generateStoryboards({ actionKey } = {}) {
    if (!storyboardPaths?.create) return

    const targetActionKeys = storyboardGenerationTargetKeys(context, actionKey)
    if (!targetActionKeys.length) return
    if (storyboardGenerationInProgress || pendingStoryboardActionKeysRef.current.size > 0) return

    const previousStoryboards = storyboards
    targetActionKeys.forEach((targetActionKey) => pendingStoryboardActionKeysRef.current.add(targetActionKey))
    setStoryboardBusy(true)
    setStoryboardError(null)
    optimisticallyMarkStoryboardsGenerating(targetActionKeys)

    fetch(pathWithQueryParam(storyboardPaths.create, "action_key", actionKey), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken()
      }
    })
      .then((response) => response.json().then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (!response.ok) throw new Error(payload.error || "Could not generate storyboards.")

        setStoryboards(Array.isArray(payload.storyboards) ? payload.storyboards : [])
        if (Array.isArray(payload.diagrams)) {
          setDiagrams(payload.diagrams)
          setDiagramError(null)
        }
      })
      .catch((error) => {
        targetActionKeys.forEach((targetActionKey) => pendingStoryboardActionKeysRef.current.delete(targetActionKey))
        setStoryboards(previousStoryboards)
        setStoryboardError(error.message || "Could not generate storyboards.")
      })
      .finally(() => setStoryboardBusy(false))
  }

  function regenerateUserAvatar() {
    if (!avatarRegenerationPath || avatarRegenerationBusy || avatarGenerationInProgress) return

    setAvatarRegenerationBusy(true)
    setAvatarRegenerationError(null)

    fetch(avatarRegenerationPath, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken()
      }
    })
      .then((response) => response.json().catch(() => ({})).then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (!response.ok) throw new Error(payload.error || "Could not regenerate a user persona avatar.")

        const nextContext = contextOverviewFromPayload(payload)
        if (nextContext) onContextUpdate?.(nextContext)
      })
      .catch((error) => {
        setAvatarRegenerationError(error.message || "Could not regenerate a user persona avatar.")
      })
      .finally(() => setAvatarRegenerationBusy(false))
  }

  function optimisticallyMarkStoryboardsGenerating(targetActionKeys) {
    const actions = limitedList(context?.actions)
    if (!actions.length) return

    const targetKeys = new Set(targetActionKeys)

    setStoryboards((currentStoryboards) => {
      const storyboardsByActionKey = new Map(
        currentStoryboards.map((storyboard) => [storyboard.actionKey, storyboard])
      )

      actions.forEach((action) => {
        const key = contextActionKey(action)
        if (!targetKeys.has(key)) return

        const currentStoryboard = storyboardsByActionKey.get(key) || {}
        storyboardsByActionKey.set(key, {
          ...currentStoryboard,
          actionKey: key,
          actionLabel: action.label || action.name || currentStoryboard.actionLabel,
          actionDescription: action.description || currentStoryboard.actionDescription,
          status: "generating",
          frames: [],
          uiFrames: []
        })
      })

      return Array.from(storyboardsByActionKey.values())
    })
  }

  function closeEditor() {
    if (saveState !== "idle") return

    setEditingItem(null)
    setSaveError(null)
    setSaveState("idle")
  }

  function closeStoryboardFrameEditor() {
    if (storyboardFrameSaveState !== "idle") return

    setEditingStoryboardFrame(null)
    setStoryboardFrameSaveError(null)
    setStoryboardFrameSaveState("idle")
  }

  function applyContextUpdatePayload(payload) {
    const nextContext = contextOverviewFromPayload(payload)
    if (nextContext) onContextUpdate?.(nextContext)
  }
  applyContextUpdatePayloadRef.current = applyContextUpdatePayload

  function saveContextItem(attributes) {
    if (!editingItem || !updatePath) return

    setSaveState("saving")
    setSaveError(null)

    patchRepositoryContext(updatePath, {
      section: editingItem.section,
      key: editingItem.key,
      attributes
    }, "Could not save context.")
      .then((payload) => {
        const nextContext = contextOverviewFromPayload(payload)
        const storyboardActionKey = actionKeyForStoryboardRegeneration(editingItem, attributes, context, nextContext)
        // Let the context-change effects clear stale storyboards before starting generation.
        if (storyboardActionKey) queuedStoryboardActionKeysRef.current.add(storyboardActionKey)
        if (nextContext) onContextUpdate?.(nextContext)
        setEditingItem(null)
        setSaveState("idle")
      })
      .catch((error) => {
        setSaveError(error.message || "Could not save context.")
        setSaveState("idle")
      })
  }

  function saveStoryboardFrame(attributes) {
    if (!editingStoryboardFrame?.updatePath) return

    const storyboard = storyboardsByKey.get(editingStoryboardFrame.actionKey)
    if (!storyboard) return
    if (!storyboardFrameEditingAvailable(storyboard)) {
      setEditingStoryboardFrame(null)
      setStoryboardFrameSaveState("idle")
      setStoryboardFrameSaveError("Storyboard must finish generating before frames can be edited.")
      return
    }

    const frameIndex = editingStoryboardFrame.frameIndex
    const frameIndexes = storyboardFrameIndexes(storyboard, storyboard.frames || [])
    const framePlan = storyboard.framePlan || []
    const nextFramePlan = frameIndexes.map((index) => {
      const frame = index === frameIndex ? attributes : framePlan[index] || {}

      return storyboardFramePlanPayload(frame)
    })
    const currentFramePayload = storyboardFramePlanPayload(framePlan[frameIndex] || {})

    if (!storyboardFrameContentsChanged(currentFramePayload, attributes)) {
      setEditingStoryboardFrame(null)
      setStoryboardFrameSaveError(null)
      setStoryboardFrameSaveState("idle")
      return
    }

    const regenerationKey = storyboardFrameRegenerationKey(editingStoryboardFrame.actionKey, frameIndex)
    let frameRegenerationQueued = false

    setStoryboardFrameSaveState("saving")
    setStoryboardFrameSaveError(null)
    setEditingStoryboardFrame(null)

    fetch(editingStoryboardFrame.updatePath, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({
        repository_context_action_storyboard: {
          frame_index: frameIndex,
          frame_plan: nextFramePlan
        }
      })
    })
      .then((response) => response.json().catch(() => ({})).then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (!response.ok) throw new Error(payload.error || "Could not save storyboard frame.")

        if (Array.isArray(payload.storyboards)) {
          setStoryboards(payload.storyboards)
          frameRegenerationQueued = storyboardPayloadGenerating(payload.storyboards, editingStoryboardFrame.actionKey)
        } else if (payload.storyboard) {
          setStoryboards((currentStoryboards) => mergeUpdatedStoryboard(currentStoryboards, payload.storyboard))
          frameRegenerationQueued = storyboardGenerationActive(payload.storyboard)
        }
        if (frameRegenerationQueued) {
          setRegeneratingStoryboardFrameKeys((currentKeys) => {
            const nextKeys = new Set(currentKeys)
            nextKeys.add(regenerationKey)
            return nextKeys
          })
        }
        setStoryboardFrameSaveState("idle")
      })
      .catch((error) => {
        setStoryboardFrameSaveError(error.message || "Could not save storyboard frame.")
        setStoryboardFrameSaveState("idle")
      })
      .finally(() => {
        if (frameRegenerationQueued) return

        setRegeneratingStoryboardFrameKeys((currentKeys) => {
          const nextKeys = new Set(currentKeys)
          nextKeys.delete(regenerationKey)
          return nextKeys
        })
      })
  }

  function deleteContextItem(attributes) {
    if (!editingItem || !updatePath || !deletableContextItem(editingItem)) return

    setSaveState("deleting")
    setSaveError(null)

    patchRepositoryContext(updatePath, {
      operation: "delete",
      section: editingItem.section,
      key: editingItem.key,
      attributes
    }, "Could not delete context item.")
      .then((payload) => {
        applyContextUpdatePayload(payload)
        setEditingItem(null)
        setSaveState("idle")
      })
      .catch((error) => {
        setSaveError(error.message || "Could not delete context item.")
        setSaveState("idle")
      })
  }

  function moveSystemObject(node, position) {
    if (!updatePath || !node?.id || !position) return

    setSaveError(null)
    onContextUpdate?.(contextWithSystemObjectPosition(context, node.id, position))
    moveSaveQueueRef.current.enqueue(`system_objects:${node.id}`, {
      attributes: systemObjectPositionAttributes(node, position),
      fallbackError: "Could not save object position.",
      key: node.id,
      section: "system_objects",
      updatePath
    })
  }

  if (!context) {
    return (
      <section className="insight-panel slide-up slide-up-delayed" aria-live="polite">
        <div className="focus-card stage-waiting-panel">
          <div className="section-label">{generating ? "Generating" : "Context"}</div>
          <p className="stage-card-title">
            {generating ? "Your context is being generated" : "No repository context yet"}
          </p>
          {generating ? <LoadingOrb className="stage-waiting-orb" size={64} state="solving" /> : null}
          {generating ? <AgentActivityFeed className="issue-thinking-stream" collapsible generating lines={streamLines} /> : null}
          <p>
            {generating
              ? "Your product context is being compiled from repository evidence."
              : "Update from repo to generate a product summary, users, actions, outcomes, and key relationships."}
          </p>
        </div>
      </section>
    )
  }

  if (selectedActionKey) {
    if (selectedAction) {
      return (
        <section className="context-overview is-action-detail slide-up slide-up-delayed">
          <ContextActionDetailPage
            action={selectedAction}
            canEdit={canEdit}
            concept={concept}
            onBack={() => {
              setEditingStoryboardFrame(null)
              updateSelectedActionKey(null)
            }}
            onEdit={(action) => openEditor({ section: "actions", key: action.key, title: action.label, item: action })}
            onEditFrame={(storyboard, frameIndex, options) => openStoryboardFrameEditor(selectedAction, storyboard, frameIndex, options)}
            regeneratingFrameKeys={regeneratingStoryboardFrameKeys}
            storyboard={storyboardsByKey.get(selectedActionKey)}
            diagrams={diagramsByKey.get(selectedActionKey) || []}
            diagramBusy={diagramBusy}
            diagramError={diagramError}
            diagramGenerationAvailable={Boolean(diagramPaths?.create)}
            diagramRegenerationEnabled={diagramRegenerationEnabled}
            onDiagramUpdate={mergeUpdatedDiagram}
            onGenerateDiagram={({ diagramType } = {}) => generateDiagram({ actionKey: selectedActionKey, diagramType })}
          />
          <ContextSaveMessage error={editingStoryboardFrame ? null : storyboardFrameSaveError} />
          <ContextStoryboardFrameEditModal
            error={storyboardFrameSaveError}
            item={editingStoryboardFrame}
            onClose={closeStoryboardFrameEditor}
            onSave={saveStoryboardFrame}
            saveState={storyboardFrameSaveState}
          />
          <ContextEditModal
            context={context}
            error={saveError}
            item={editingItem}
            onClose={closeEditor}
            onDelete={deleteContextItem}
            onSave={saveContextItem}
            saveState={saveState}
          />
        </section>
      )
    }
  }

  if (selectedPersonaKey) {
    if (selectedPersona) {
      return (
        <section className="context-overview is-persona-detail slide-up slide-up-delayed">
          <ContextPersonaDetailPage
            actions={actionItems}
            canEdit={canEdit}
            concept={concept}
            issues={issues}
            onBack={() => updateSelectedPersonaKey(null)}
            onEdit={(persona) => openEditor({ section: "users", key: persona.key, title: personaDisplayName(persona), item: persona })}
            onOpenAction={(action) => {
              updateSelectedPersonaKey(null)
              updateSelectedActionKey(contextActionKey(action))
            }}
            persona={selectedPersona}
            users={context.users}
          />
          <ContextSaveMessage error={editingItem ? null : saveError} />
          <ContextEditModal
            context={context}
            error={saveError}
            item={editingItem}
            onClose={closeEditor}
            onDelete={deleteContextItem}
            onSave={saveContextItem}
            saveState={saveState}
          />
        </section>
      )
    }
  }

  return (
    <section className="context-overview slide-up slide-up-delayed">
      <ProductSummarySection
        canEdit={canEdit}
        onEdit={() => openEditor({ section: "product_summary", title: "Product Summary", item: context.productSummary })}
        summary={context.productSummary}
      />
      <ActionsSection
        actions={actionItems}
        canEdit={canEdit}
        canShowStoryboardControl={Boolean(storyboardPaths?.index && actionItems.length && !allActionStoryboardsReady)}
        canGenerateStoryboards={Boolean(storyboardPaths?.create && actionItems.length)}
        onAdd={openNewAction}
        onEdit={(action) => openEditor({ section: "actions", key: action.key, title: action.label, item: action })}
        onEmphasisTargetChange={onEmphasisTargetChange}
        onGenerateStoryboards={generateStoryboards}
        onOpenDetail={(action) => updateSelectedActionKey(contextActionKey(action))}
        storyboardBusy={storyboardBusy}
        storyboardError={storyboardError}
        storyboardsByKey={storyboardsByKey}
        storyboardsGenerating={storyboardsGenerating}
        users={context.users}
      />
      <UsersSection
        avatarBusy={avatarRegenerationBusy || avatarGenerationInProgress}
        avatarError={avatarRegenerationError}
        canEdit={canEdit}
        canRegenerateAvatars={Boolean(avatarRegenerationEnabled && avatarRegenerationPath)}
        onAdd={openNewUser}
        onEdit={(user) => openEditor({ section: "users", key: user.key, title: personaDisplayName(user), item: user })}
        onEmphasisTargetChange={onEmphasisTargetChange}
        onOpenDetail={(user) => {
          updateSelectedActionKey(null)
          updateSelectedPersonaKey(contextPersonaKey(user))
        }}
        onRegenerateAvatars={regenerateUserAvatar}
        users={context.users}
      />
      <SystemSection
        canEdit={canEdit}
        onAddObject={openNewSystemObject}
        onAddRelationship={openNewSystemRelationship}
        onEdit={openEditor}
        onMoveObject={moveSystemObject}
        system={context.system}
      />
      <ContextSaveMessage error={editingItem ? null : saveError} />
      <ContextEditModal
        context={context}
        error={saveError}
        item={editingItem}
        onClose={closeEditor}
        onDelete={deleteContextItem}
        onSave={saveContextItem}
        saveState={saveState}
      />
    </section>
  )
}

export function ContextRefreshButton({ busy = false, disabled = false, lastUpdatedAt = null, onClick }) {
  const updateLabel = busy ? "Syncing now" : contextLastSyncedLabel(lastUpdatedAt)

  return (
    <div className="context-refresh-control">
      <PullLatestButton
        busy={busy}
        className="context-refresh-button"
        disabled={disabled}
        onClick={onClick}
        label="Sync code"
        busyLabel="Syncing code"
        title="Sync latest code from the repository"
      />
      <span className="context-refresh-timestamp">{updateLabel}</span>
    </div>
  )
}

function ProductSummarySection({ canEdit = false, onEdit, summary = {} }) {
  return (
    <section className="context-product-lead" id="context-overview">
      <span className="context-band-kicker">Why</span>
      <p>{summary.description || "Unknown from repo."}</p>
      {canEdit ? <EditContextButton label="Edit product summary" onClick={onEdit} /> : null}
    </section>
  )
}

function UsersSection({
  avatarBusy = false,
  avatarError = null,
  canEdit = false,
  canRegenerateAvatars = false,
  onAdd,
  onEdit,
  onEmphasisTargetChange = null,
  onOpenDetail,
  onRegenerateAvatars,
  users = []
}) {
  const items = limitedList(users)
  const panelAction = canEdit || canRegenerateAvatars ? (
    <ContextPanelActions>
      {canEdit ? <AddContextButton label="Add user" onClick={onAdd} /> : null}
      {canRegenerateAvatars ? (
        <RegenerateUserAvatarsButton
          busy={avatarBusy}
          disabled={!items.length || avatarBusy}
          onClick={onRegenerateAvatars}
        />
      ) : null}
    </ContextPanelActions>
  ) : null

  if (!items.length && !canEdit) return null

  return (
    <ContextPanel
      action={panelAction}
      id="context-users"
      kicker="Who"
      title="User Personas"
    >
      {avatarError ? <p className="context-storyboard-error context-avatar-error" role="alert">{avatarError}</p> : null}
      {items.length ? (
        <div className="context-user-grid">
          {items.map((user, index) => (
            <UserPersonaCard
              canEdit={canEdit}
              index={index}
              key={contextPersonaKey(user) || index}
              onEdit={onEdit}
              onEmphasisTargetChange={onEmphasisTargetChange}
              onOpenDetail={onOpenDetail}
              user={user}
              users={items}
            />
          ))}
        </div>
      ) : <ContextEmptyState label="No user groups yet." />}
    </ContextPanel>
  )
}

function UserPersonaCard({ canEdit = false, index = 0, onEdit, onEmphasisTargetChange = null, onOpenDetail, user, users = [] }) {
  const displayName = personaDisplayName(user)
  const roleName = personaRoleName(user)
  const archetypeName = personaArchetypeName(user)
  const essence = personaCardEssence(user)
  const relationships = personaRelationshipItems(user, users)
  const personaKey = contextPersonaKey(user)
  const emphasisTarget = personaKey ? { key: personaKey, type: "persona" } : null

  return (
    <article
      className="context-user-card"
      onBlur={() => onEmphasisTargetChange?.(null)}
      onFocus={() => onEmphasisTargetChange?.(emphasisTarget)}
      onPointerEnter={() => onEmphasisTargetChange?.(emphasisTarget)}
      onPointerLeave={() => onEmphasisTargetChange?.(null)}
    >
      <button
        type="button"
        className="context-user-open-card"
        onClick={() => onOpenDetail?.(user)}
        aria-label={`Open persona board for ${displayName}`}
      />
      {canEdit ? (
        <EditContextButton
          className="context-card-edit-button"
          label={`Edit ${displayName}`}
          onClick={() => onEdit(user)}
        />
      ) : null}

      <header className="context-persona-card-header">
        <PersonaAvatar user={user} index={index} />
        <div className="context-persona-card-identity">
          <h3>{displayName}</h3>
          {archetypeName || roleName ? (
            <p className="context-persona-card-meta">
              {archetypeName ? <span>{archetypeName}</span> : null}
              {roleName && roleName !== archetypeName ? <span>{roleName}</span> : null}
            </p>
          ) : null}
        </div>
      </header>

      {essence ? <p className="context-persona-card-essence">{essence}</p> : null}

      {relationships.length ? (
        <div className="context-user-card-relationships" aria-label={`${displayName} relationships`}>
          {relationships.map((relationship, relationshipIndex) => {
            const chipLabel = relationship.name || relationship.relationship
            if (!chipLabel) return null

            const chipTitle = relationship.name && relationship.relationship
              ? `${relationship.relationship} · ${relationship.name}`
              : chipLabel

            // Editing a relationship reopens the persona editor, the same
            // target the retired graph's edge click used.
            return canEdit ? (
              <button
                type="button"
                className="context-user-relationship-chip is-editable"
                key={`${chipLabel}-${relationshipIndex}`}
                onClick={() => onEdit?.(user)}
                title={chipTitle}
              >
                {chipLabel}
              </button>
            ) : (
              <span className="context-user-relationship-chip" key={`${chipLabel}-${relationshipIndex}`} title={chipTitle}>
                {chipLabel}
              </span>
            )
          })}
        </div>
      ) : null}
    </article>
  )
}

function RegenerateUserAvatarsButton({ busy = false, disabled = false, onClick }) {
  const label = busy ? "Generating" : "Regenerate avatars"

  return (
    <button
      type="button"
      className={[ "context-storyboard-button", busy ? "is-busy" : null, disabled ? "is-disabled" : null ].filter(Boolean).join(" ")}
      disabled={busy || disabled}
      onClick={onClick}
      aria-label="Regenerate next user persona avatar"
    >
      {busy ? <LoadingOrb state="shaping" theme="light" /> : <Repeat aria-hidden="true" strokeWidth={1.8} />}
      <span>{label}</span>
    </button>
  )
}

export function PersonaAvatar({ className = "", decorative = false, imageLoading = "eager", index = 0, user = {} }) {
  const tone = personaToneFor(user, index)
  const avatar = user.avatar || {}
  const status = avatar.status || "idle"
  const label = avatar.alt || `${personaDisplayName(user)} persona avatar`

  return (
    <span
      className={[ "context-persona-avatar", `is-${tone.key}`, `is-${status}`, className ].filter(Boolean).join(" ")}
      aria-hidden={decorative ? "true" : undefined}
      aria-label={!decorative && !avatar.url ? label : undefined}
      style={{
        "--persona-bg": tone.background,
        "--persona-accent": tone.accent
      }}
    >
      {avatar.url ? (
        <img src={avatar.url} alt={decorative ? "" : label} loading={imageLoading} decoding="sync" fetchPriority={imageLoading === "eager" ? "high" : undefined} />
      ) : (
        <span className="context-persona-avatar-initials">{personaInitials(personaDisplayName(user))}</span>
      )}
      {status === "generating" ? <LoadingOrb className="context-persona-avatar-orb" state="shaping" /> : null}
    </span>
  )
}

export function ContextPersonaDetailPage({
  actions = [],
  canEdit = false,
  concept = null,
  embedded = false,
  issues = [],
  onEdit,
  onOpenAction,
  persona = {},
  users = []
}) {
  const quote = personaHeadlineQuote(persona)
  const displayName = personaDisplayName(persona)
  const roleName = personaRoleName(persona)
  const archetype = personaArchetypeName(persona)
  const personaTitle = roleName || displayName
  const portraitName = personaTitle !== displayName ? displayName : ""
  const narrative = personaText(persona, "narrative", "story", "description")
  const howToSpot = personaText(persona, "howToSpot", "how_to_spot")
  const goals = personaGoalItems(persona)
  const careAbouts = personaArray(persona, "careAbouts", "care_abouts")
  const topIssues = personaArray(persona, "topIssues", "top_issues")
  const painPoints = personaArray(persona, "painPoints", "pain_points")
  const issueItems = topIssues.length ? topIssues : personaArray(persona, "painPoints", "pain_points", "criticalNeeds", "critical_needs")
  const constraints = personaArray(persona, "constraints")
  const decisionCriteria = personaArray(persona, "decisionCriteria", "decision_criteria")
  const primaryGoal = goals[0] || ""
  const supportingGoals = primaryGoal ? goals.slice(1) : goals
  const facts = personaFactItems(persona)
  const relationships = personaRelationshipItems(persona, users)
  const relatedActions = personaRelatedActions(persona, actions)
  const relatedIssues = personaRelatedIssues(persona, issues)
  const relatedMoves = personaRelatedConceptMoves(persona, concept)
  const introParagraphs = uniqueTextItems([ persona.role, narrative ])
  const personaLabelItems = [ archetype ].filter(Boolean)
  const goalGroups = [
    { title: "Main goal", items: primaryGoal ? [ primaryGoal ] : [] },
    { title: "Other goals", items: supportingGoals },
    { title: "Care drivers", items: careAbouts }
  ]
  const issueGroups = [
    { title: "Top issues", items: issueItems },
    ...(topIssues.length && painPoints.length ? [{ title: "Pain points", items: painPoints }] : []),
    { title: "Constraints", items: constraints }
  ]
  const decisionGroups = [
    { title: "Says yes when", items: decisionCriteria },
    { title: "Relationships", relationships }
  ]

  return (
    <article className={`context-persona-detail ${embedded ? "is-embedded" : ""}`.trim()}>
      <header className={`context-persona-detail-header detail-hero ${howToSpot ? "has-reference" : ""}`.trim()}>
        <div className="context-persona-portrait-cluster">
          <PersonaAvatar className="context-persona-detail-avatar" user={persona} />
          {portraitName ? <p className="context-persona-portrait-name">{portraitName}</p> : null}
        </div>
        <div className="context-action-detail-title context-persona-detail-identity">
          <div className="context-action-detail-title-copy">
            <h2>{personaTitle}</h2>
            {personaLabelItems.length ? (
              <p className="context-persona-detail-meta">
                {personaLabelItems.map((item, index) => <span key={`${item}-${index}`}>{item}</span>)}
              </p>
            ) : null}
            {quote ? <blockquote className="context-persona-quote"><p>"{quote}"</p></blockquote> : null}
            {introParagraphs.length ? (
              <div className="context-persona-intro-copy">
                {introParagraphs.map((paragraph, index) => <p key={`${paragraph}-${index}`}>{paragraph}</p>)}
              </div>
            ) : null}
          </div>
          <div className="detail-hero-actions">
            <DetailShareButton />
            {canEdit ? (
              <EditContextButton
                className="context-action-detail-edit-button"
                label={`Edit ${personaTitle || displayName || "persona"}`}
                onClick={() => onEdit?.(persona)}
              />
            ) : null}
          </div>
        </div>
        {howToSpot ? (
          <aside className="context-persona-spot-panel">
            <h3>{`How to spot ${portraitName || personaTitle || displayName}`}</h3>
            <p>{howToSpot}</p>
          </aside>
        ) : null}
      </header>

      <section className="context-persona-sheet" aria-label={`${personaTitle || displayName} persona details`}>
        <ContextPersonaSheetSection groups={goalGroups} title="Goals" variant="goals" />
        <ContextPersonaSheetSection groups={issueGroups} title="Top issues" variant="issues" />
        <ContextPersonaSheetSection groups={decisionGroups} title="Decision factors" variant="decision" />
        <ContextPersonaAboutSection facts={facts} name={portraitName || displayName || personaTitle} />
      </section>

      <ContextPersonaRelatedWork
        actions={relatedActions}
        issues={relatedIssues}
        moves={relatedMoves}
        onOpenAction={onOpenAction}
      />
    </article>
  )
}

function ContextPersonaSheetSection({ groups = [], title, variant = "" }) {
  const visibleGroups = groups.map((group) => {
    const relationships = Array.isArray(group.relationships) ? group.relationships : []
    const items = uniqueTextItems(group.items || [])
    return { ...group, items, relationships }
  }).filter((group) => group.items.length || group.relationships.length)

  if (!visibleGroups.length) return null

  return (
    <section className={`context-persona-sheet-section ${variant ? `is-${variant}` : ""}`.trim()}>
      <h3>{title}</h3>
      <div className="context-persona-sheet-groups">
        {visibleGroups.map((group) => (
          <div className="context-persona-sheet-group" key={group.title}>
            <h4>{group.title}</h4>
            {group.relationships.length ? (
              <ContextPersonaRelationshipList relationships={group.relationships} />
            ) : (
              <ul>
                {group.items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{item}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

function ContextPersonaRelationshipList({ relationships = [] }) {
  if (!relationships.length) return null

  return (
    <ul>
      {relationships.map((relationship, index) => (
        <li key={`${relationship.name || relationship.personaKey || "relationship"}-${index}`}>
          <strong>{relationship.name || relationship.personaKey}</strong>
          {relationship.relationship ? `: ${relationship.relationship}` : null}
        </li>
      ))}
    </ul>
  )
}

function ContextPersonaAboutSection({ facts = [], name = "" }) {
  if (!facts.length) return null

  return (
    <aside className="context-persona-sheet-section is-about">
      <h3>{name ? `About ${name}` : "About"}</h3>
      <dl className="context-persona-fact-list">
        {facts.map((fact, index) => (
          <div key={`${fact.label || "fact"}-${index}`}>
            {fact.label ? <dt>{fact.label}</dt> : null}
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </aside>
  )
}

function ContextPersonaRelatedWork({ actions = [], issues = [], moves = [], onOpenAction }) {
  if (!actions.length && !issues.length && !moves.length) return null

  return (
    <section className="context-persona-related-work">
      <div className="context-action-detail-list-heading">
        <Route aria-hidden="true" strokeWidth={1.8} />
        <h3>Connected planning work</h3>
      </div>
      <div className="context-persona-related-grid">
        <ContextPersonaRelatedColumn
          emptyLabel="No workflows reference this persona yet."
          icon={Workflow}
          items={actions}
          label="Workflows"
          renderItem={(action) => (
            <button type="button" className="context-persona-related-item" onClick={() => onOpenAction?.(action)}>
              <strong>{action.label || action.name}</strong>
              {action.description ? <span>{action.description}</span> : null}
            </button>
          )}
        />
        <ContextPersonaRelatedColumn
          emptyLabel="No issues are tagged to this persona yet."
          icon={Inbox}
          items={issues}
          label="Issues"
          renderItem={(issue) => {
            const body = (
              <>
                <strong>{issue.title}</strong>
                {issue.description ? <span>{issue.description}</span> : null}
              </>
            )

            return issue.detailPath ? (
              <a className="context-persona-related-item" href={issue.detailPath}>{body}</a>
            ) : (
              <div className="context-persona-related-item">{body}</div>
            )
          }}
        />
        <ContextPersonaRelatedColumn
          emptyLabel="No recommendations carry this persona lens yet."
          icon={Lightbulb}
          items={moves}
          label="Recommendations"
          renderItem={(move) => {
            const body = (
              <>
                <strong>{move.title}</strong>
                {move.rationale ? <span>{move.rationale}</span> : null}
                {move.conceptTitle ? <small>{move.conceptTitle}</small> : null}
              </>
            )

            return move.detailPath ? (
              <a className="context-persona-related-item" href={move.detailPath}>{body}</a>
            ) : (
              <div className="context-persona-related-item">{body}</div>
            )
          }}
        />
      </div>
    </section>
  )
}

function ContextPersonaRelatedColumn({ emptyLabel, icon: Icon, items = [], label, renderItem }) {
  return (
    <section className="context-persona-related-column">
      <div className="context-persona-related-column-heading">
        <Icon aria-hidden="true" strokeWidth={1.8} />
        <h4>{label}</h4>
      </div>
      {items.length ? items.map((item, index) => (
        <div key={item.key || item.id || item.title || item.label || index}>
          {renderItem(item, index)}
        </div>
      )) : <p>{emptyLabel}</p>}
    </section>
  )
}

function ActionsSection({
  actions = [],
  canEdit = false,
  canGenerateStoryboards = false,
  canShowStoryboardControl = false,
  onAdd,
  onEdit,
  onEmphasisTargetChange = null,
  onGenerateStoryboards,
  onOpenDetail,
  storyboardBusy = false,
  storyboardError = null,
  storyboardsByKey = new Map(),
  storyboardsGenerating = false,
  users = []
}) {
  const items = limitedList(actions)
  const panelAction = canEdit || canShowStoryboardControl ? (
    <ContextPanelActions>
      {canEdit ? <AddContextButton label="Add action" onClick={onAdd} /> : null}
      {canShowStoryboardControl ? (
        <ContextStoryboardButton
          busy={storyboardBusy || storyboardsGenerating}
          disabled={!canGenerateStoryboards || storyboardBusy || storyboardsGenerating}
          onClick={onGenerateStoryboards}
        />
      ) : null}
    </ContextPanelActions>
  ) : null

  if (!items.length && !canEdit) return null

  return (
    <ContextPanel
      action={panelAction}
      id="context-actions"
      kicker="How"
      title="User Actions"
    >
      {items.length ? (
        <ContextActionStoryboards
          actions={items}
          canEdit={canEdit}
          error={storyboardError}
          onEdit={onEdit}
          onEmphasisTargetChange={onEmphasisTargetChange}
          onOpenDetail={onOpenDetail}
          storyboardsByKey={storyboardsByKey}
          users={users}
        />
      ) : <ContextEmptyState label="No user actions yet." />}
    </ContextPanel>
  )
}

function ContextStoryboardButton({ busy = false, disabled = false, onClick }) {
  const label = busy ? "Generating" : "Generate storyboards"

  return (
    <button
      type="button"
      className={[ "context-storyboard-button", busy ? "is-busy" : null, disabled ? "is-disabled" : null ].filter(Boolean).join(" ")}
      disabled={busy || disabled}
      onClick={onClick}
    >
      {busy ? <LoadingOrb state="shaping" theme="light" /> : <Sparkles aria-hidden="true" strokeWidth={1.8} />}
      <span>{label}</span>
    </button>
  )
}

function ContextActionStoryboards({
  actions = [],
  canEdit = false,
  error = null,
  onEdit,
  onEmphasisTargetChange = null,
  onOpenDetail,
  storyboardsByKey = new Map(),
  users = []
}) {
  return (
    <div className="context-storyboard-list" aria-label="Action storyboards">
      {error ? <p className="context-storyboard-error" role="alert">{error}</p> : null}
      {actions.map((action) => (
        <ContextActionStoryboard
          action={action}
          canEdit={canEdit}
          key={contextActionKey(action)}
          onEdit={onEdit}
          onEmphasisTargetChange={onEmphasisTargetChange}
          onOpenDetail={onOpenDetail}
          storyboard={storyboardsByKey.get(contextActionKey(action))}
          users={users}
        />
      ))}
    </div>
  )
}

function ContextActionStoryboard({ action, canEdit = false, onEdit, onEmphasisTargetChange = null, onOpenDetail, storyboard, users = [] }) {
  const frames = storyboard?.frames || []
  const firstFrame = frames[0]
  const ready = storyboardReady(storyboard)
  const status = storyboardStatus(storyboard)
  const actionDescription = action.description || storyboard?.actionDescription
  const personaUsers = actionPersonaUsers(action, users)
  const actionKey = contextActionKey(action)
  const emphasisTarget = actionKey ? { key: actionKey, type: "action" } : null

  return (
    <article
      className={[ "context-storyboard", ready ? "is-ready" : null, personaUsers.length ? "has-persona-avatars" : null ].filter(Boolean).join(" ")}
      onBlur={() => onEmphasisTargetChange?.(null)}
      onFocus={() => onEmphasisTargetChange?.(emphasisTarget)}
      onPointerEnter={() => onEmphasisTargetChange?.(emphasisTarget)}
      onPointerLeave={() => onEmphasisTargetChange?.(null)}
    >
      <button
        type="button"
        className="context-storyboard-open-card"
        onClick={() => onOpenDetail?.(action)}
        aria-label={`Open detail view for ${action.label}`}
      />
      <div className="context-storyboard-media">
        <div className="context-storyboard-frame is-ideal">
          <div className="context-storyboard-frame-image">
            {firstFrame ? (
              <img
                src={firstFrame.thumbnailUrl || firstFrame.url}
                alt={firstFrame.alt || `${action.label} storyboard frame 1`}
                loading="lazy"
              />
            ) : (
              <ContextStoryboardPlaceholder status={status} />
            )}
          </div>
        </div>
        {canEdit ? (
          <div className="context-storyboard-card-actions">
            <EditContextButton
              className="context-storyboard-edit-button"
              label={`Edit ${action.label}`}
              onClick={() => onEdit?.(action)}
            />
          </div>
        ) : null}
      </div>

      <div className="context-storyboard-card-copy">
        <h3>{action.label}</h3>
        {actionDescription ? <p>{actionDescription}</p> : null}
      </div>
      <ActionPersonaAvatars action={action} users={personaUsers} />
    </article>
  )
}

export function ActionPersonaAvatars({ action, users = [] }) {
  const [expanded, setExpanded] = useState(false)
  if (!users.length) return null

  const visibleUsers = expanded ? users : users.slice(0, maximumActionPersonaAvatars)
  const overflowCount = users.length - visibleUsers.length
  const label = `${action.label || "Action"} personas: ${users.map((user) => user.name).filter(Boolean).join(", ")}`

  function expandAvatars(event) {
    event.preventDefault()
    event.stopPropagation()
    setExpanded(true)
  }

  return (
    <div
      className={`context-action-persona-avatars ${expanded ? "is-expanded" : ""}`.trim()}
      aria-label={label}
      role="group"
      title={label}
    >
      {visibleUsers.map((user, index) => (
        <PersonaAvatar
          className="context-action-persona-avatar"
          decorative
          imageLoading="lazy"
          index={index}
          key={user.key || user.name || index}
          user={user}
        />
      ))}
      {overflowCount > 0 ? (
        <button
          type="button"
          className="context-action-persona-overflow"
          aria-label={`Show all personas for ${action.label || "this action"}`}
          aria-expanded={expanded}
          onClick={expandAvatars}
        >
          +{overflowCount}
        </button>
      ) : null}
    </div>
  )
}

export function ContextActionDetailPage({
  action,
  canEdit = false,
  concept = null,
  diagramBusy = false,
  diagramError = null,
  diagramGenerationAvailable = false,
  diagramRegenerationEnabled = false,
  embedded = false,
  onDiagramUpdate,
  onEdit,
  onEditFrame,
  onGenerateDiagram,
  regeneratingFrameKeys = new Set(),
  diagrams = [],
  storyboard
}) {
  const actionKey = contextActionKey(action)
  const frames = storyboard?.frames || []
  const framePlan = storyboard?.framePlan || []
  const uiFrames = storyboard?.uiFrames || []
  const uiFramePlan = storyboard?.uiFramePlan || []
  const frameIndexes = storyboardFrameIndexes(storyboard, frames)
  const heroFrame = frameIndexes.length ? frames[frameIndexes[0]] : null
  const uiFrameIndexes = storyboardFrameIndexes({ framePlan: uiFramePlan }, uiFrames)
  const framesEditable = Boolean(canEdit && storyboardFrameEditingAvailable(storyboard))
  const modelItems = journeyModelItems(action)
  const workflowItems = journeyWorkflowItems(action)
  const status = storyboardStatus(storyboard)
  const [selectedFrameSelection, setSelectedFrameSelection] = useState(null)
  const selectedFrameKind = selectedFrameSelection?.kind === "ui" ? "ui" : "journey"
  const selectedFrameIndexes = selectedFrameKind === "ui" ? uiFrameIndexes : frameIndexes
  const selectedFrames = selectedFrameKind === "ui" ? uiFrames : frames
  const selectedFramePlan = selectedFrameKind === "ui" ? uiFramePlan : framePlan
  const selectedFramesEditable = selectedFrameKind === "journey" && framesEditable
  const selectedFrameIndex = selectedFrameSelection?.frameIndex ?? null
  const selectedFrameVisibleIndex = selectedFrameIndex === null ? -1 : selectedFrameIndexes.indexOf(selectedFrameIndex)
  const selectedFrame = selectedFrameVisibleIndex >= 0 ? selectedContextActionFrameItem({
    action,
    frameIndex: selectedFrameIndex,
    frames: selectedFrames,
    framesEditable: selectedFramesEditable,
    onEditFrame,
    plannedFrame: selectedFramePlan[selectedFrameIndex] || {},
    storyboard,
    visibleIndex: selectedFrameVisibleIndex
  }) : null

  function selectFrame(kind, frameIndex) {
    const targetFrames = kind === "ui" ? uiFrames : frames
    if (!targetFrames[frameIndex]) return

    setSelectedFrameSelection({ frameIndex, kind })
  }

  function selectFrameAtVisibleIndex(kind, visibleIndex) {
    const targetFrameIndexes = kind === "ui" ? uiFrameIndexes : frameIndexes
    const frameIndex = targetFrameIndexes[visibleIndex]

    if (frameIndex === undefined) return

    selectFrame(kind, frameIndex)
  }

  return (
    <article className={`context-action-detail ${embedded ? "is-embedded" : ""}`.trim()}>
      <header className="context-action-detail-header detail-hero">
        <div className="detail-hero-media">
          {heroFrame ? (
            <img src={heroFrame.thumbnailUrl || heroFrame.url} alt="" aria-hidden="true" />
          ) : (
            <div className="detail-hero-placeholder" aria-hidden="true"><Image /></div>
          )}
        </div>
        <div className="context-action-detail-title detail-hero-copy">
          <div className="context-action-detail-title-copy">
            <h2>{action.label}</h2>
            {action.description ? <p>{action.description}</p> : null}
          </div>
          <div className="detail-hero-actions">
            <DetailShareButton />
            {canEdit ? (
              <EditContextButton
                className="context-action-detail-edit-button"
                label={`Edit ${action.label}`}
                onClick={() => onEdit?.(action)}
              />
            ) : null}
          </div>
        </div>
      </header>

      <ContextActionFrameRow
        action={action}
        actionKey={actionKey}
        editable={framesEditable}
        frameIndexes={frameIndexes}
        framePlan={framePlan}
        frames={frames}
        kind="journey"
        onEditFrame={(index, visibleIndex) => onEditFrame?.(storyboard, index, { displayFrameIndex: visibleIndex })}
        onSelectFrame={(index) => selectFrame("journey", index)}
        regeneratingFrameKeys={regeneratingFrameKeys}
        status={status}
        title="User journey"
      />

      <ContextActionFrameRow
        action={action}
        actionKey={actionKey}
        frameIndexes={uiFrameIndexes}
        framePlan={uiFramePlan}
        frames={uiFrames}
        kind="ui"
        onSelectFrame={(index) => selectFrame("ui", index)}
        status={status}
        title="Product UI"
      />

      <ContextActionFrameModal
        frameItem={selectedFrame}
        frameTotal={selectedFrameIndexes.length}
        onClose={() => setSelectedFrameSelection(null)}
        onNext={selectedFrameVisibleIndex >= 0 && selectedFrameVisibleIndex < selectedFrameIndexes.length - 1 ? () => selectFrameAtVisibleIndex(selectedFrameKind, selectedFrameVisibleIndex + 1) : null}
        onPrevious={selectedFrameVisibleIndex > 0 ? () => selectFrameAtVisibleIndex(selectedFrameKind, selectedFrameVisibleIndex - 1) : null}
      />

      <ContextActionTechnicalPanel
        action={action}
        busy={diagramBusy}
        canGenerate={diagramGenerationAvailable}
        canRegenerate={diagramRegenerationEnabled}
        collapseReadyDiagram
        detailSection
        diagrams={diagrams}
        error={diagramError}
        modelItems={modelItems}
        onDiagramUpdate={onDiagramUpdate}
        onGenerate={onGenerateDiagram}
        workflowItems={workflowItems}
      />
    </article>
  )
}

function ContextActionFrameRow({
  action,
  actionKey,
  editable = false,
  frameIndexes = [],
  framePlan = [],
  frames = [],
  kind = "journey",
  onEditFrame,
  onSelectFrame,
  regeneratingFrameKeys = new Set(),
  status,
  title
}) {
  if (!frameIndexes.length) return null

  return (
    <section
      className={`context-action-detail-frame-row is-${kind}`.trim()}
      aria-label={`${action.label} ${title.toLowerCase()} frames`}
      style={{ "--context-action-detail-frame-count": frameIndexes.length }}
    >
      <h3>{title}</h3>
      <div className="context-action-detail-frames">
        {frameIndexes.map((index, visibleIndex) => {
          const frame = frames[index]
          const plannedFrame = framePlan[index] || {}
          const frameRegenerating = kind === "journey" && regeneratingFrameKeys.has(storyboardFrameRegenerationKey(actionKey, index))

          return (
            <figure
              className={[
                "context-action-detail-frame",
                frameRegenerating ? "is-regenerating" : null
              ].filter(Boolean).join(" ")}
              key={index}
            >
              {frame && onSelectFrame ? (
                <button
                  type="button"
                  className={`context-action-detail-image ${frameRegenerating ? "is-regenerating" : ""}`.trim()}
                  onClick={() => onSelectFrame(index)}
                  aria-label={`Open ${action.label} ${title.toLowerCase()} frame ${visibleIndex + 1}`}
                >
                  <img
                    src={frame.thumbnailUrl || frame.url}
                    alt={frame.alt || `${action.label} ${title.toLowerCase()} frame ${index + 1}`}
                    loading="lazy"
                  />
                  {frameRegenerating ? (
                    <div className="context-action-detail-image-loading" role="status" aria-label="Regenerating frame">
                      <LoadingOrb state="shaping" />
                    </div>
                  ) : null}
                </button>
              ) : frame ? (
                <div className="context-action-detail-image">
                  <img
                    src={frame.thumbnailUrl || frame.url}
                    alt={frame.alt || `${action.label} ${title.toLowerCase()} frame ${index + 1}`}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className={`context-action-detail-image ${frameRegenerating ? "is-regenerating" : ""}`.trim()}>
                  <ContextStoryboardPlaceholder status={status} />
                </div>
              )}
              <StoryboardFrameCopy
                className="context-action-detail-frame-copy"
                description={kind === "ui" ? contextActionProductShotLabel(plannedFrame) : plannedFrame.description}
                title={kind === "ui" ? null : plannedFrame.title}
              />
              {editable ? (
                <EditContextButton
                  className="context-frame-edit-button"
                  label={`Edit ${action.label} frame ${visibleIndex + 1} text`}
                  onClick={() => onEditFrame?.(index, visibleIndex)}
                />
              ) : null}
            </figure>
          )
        })}
      </div>
    </section>
  )
}

export function ContextActionTechnicalPanel({
  action,
  busy = false,
  canGenerate = false,
  canRegenerate = false,
  collapseReadyDiagram = false,
  detailSection = false,
  diagrams = [],
  error = null,
  modelItems = [],
  onDiagramUpdate,
  onGenerate,
  workflowItems = []
}) {
  const diagramItems = diagramList(diagrams)
  const availableDiagrams = useMemo(() => readyActionDiagrams(diagrams), [diagrams])
  const [selectedDiagramId, setSelectedDiagramId] = useState(null)
  const diagramHeadingId = useId()
  const selectedDiagram = availableDiagrams.find((diagram) => diagram.id === selectedDiagramId) || availableDiagrams[0]
  const generatingDiagram = diagramItems.find((diagram) => diagram.status === "generating")
  const failedDiagram = diagramItems.find((diagram) => diagram.status === "failed")
  const activeStreamDiagram = generatingDiagram || failedDiagram
  const activeStreamKey = diagramStreamKey(activeStreamDiagram)
  const onDiagramUpdateRef = useRef(onDiagramUpdate)
  const autoGeneratedDiagramKeysRef = useRef(new Set())
  const [streamEvents, setStreamEvents] = useState(() => {
    return mergeDiagramStreamEvents([], activeStreamDiagram?.streamEvents || [])
  })
  const streamSequenceRef = useRef(lastDiagramStreamSequence(streamEvents))
  const autoGenerateDiagramKey = emptyActionDiagramGenerationKey({
    action,
    busy,
    canGenerate,
    failedDiagram,
    error,
    generatingDiagram,
    selectedDiagram
  })

  useEffect(() => {
    onDiagramUpdateRef.current = onDiagramUpdate
  }, [onDiagramUpdate])

  useEffect(() => {
    if (!autoGenerateDiagramKey) return
    if (typeof onGenerate !== "function") return
    if (autoGeneratedDiagramKeysRef.current.has(autoGenerateDiagramKey)) return

    autoGeneratedDiagramKeysRef.current.add(autoGenerateDiagramKey)
    onGenerate?.({ diagramType: "workflow" })
  }, [autoGenerateDiagramKey, onGenerate])

  useEffect(() => {
    const initialEvents = mergeDiagramStreamEvents([], activeStreamDiagram?.streamEvents || [])

    setStreamEvents(initialEvents)
    streamSequenceRef.current = lastDiagramStreamSequence(initialEvents)
  }, [activeStreamKey])

  useEffect(() => {
    if (!generatingDiagram?.eventsUrl) return undefined

    let cancelled = false

    function pollDiagramEvents() {
      const url = new URL(generatingDiagram.eventsUrl, window.location.origin)
      url.searchParams.set("after", streamSequenceRef.current)

      fetch(url.toString(), {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Cache-Control": "no-store"
        }
      })
        .then((response) => {
          if (!response.ok) throw new Error("Could not read diagram events.")
          return response.json()
        })
        .then((payload) => {
          if (cancelled) return

          const incomingEvents = Array.isArray(payload.events) ? payload.events : []
          if (incomingEvents.length > 0) {
            streamSequenceRef.current = lastDiagramStreamSequence(incomingEvents, streamSequenceRef.current)
            setStreamEvents((currentEvents) => mergeDiagramStreamEvents(currentEvents, incomingEvents))
          } else if (payload.next_sequence !== undefined) {
            streamSequenceRef.current = Number(payload.next_sequence) || streamSequenceRef.current
          }

          if (payload.diagram) onDiagramUpdateRef.current?.(payload.diagram)
        })
        .catch(() => {})
    }

    pollDiagramEvents()
    const interval = window.setInterval(pollDiagramEvents, 1000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [generatingDiagram?.eventsUrl])

  if (!selectedDiagram || failedDiagram) {
    if (canGenerate || generatingDiagram || failedDiagram || error) {
      const isGenerating = Boolean(generatingDiagram)
      const generationInProgress = busy || isGenerating
      const failed = Boolean(failedDiagram || error)
      const failedMessage = diagramFailureMessage(failedDiagram, error, streamEvents)
      const visibleStreamEvents = failed && !generationInProgress
        ? diagramStreamEventsWithFailure(streamEvents, failedMessage)
        : streamEvents
      const diagramStreamLines = architectureThinkingLines({
        events: visibleStreamEvents,
        status: generationInProgress ? "Starting architecture diagram generation." : failedMessage,
        working: generationInProgress
      })
      const showStream = generationInProgress || failed
      const showGenerationButton = canGenerate && !generationInProgress

      return (
        <section
          className={[
            "context-action-diagram-panel",
            detailSection ? "is-detail-section" : null
          ].filter(Boolean).join(" ")}
          aria-labelledby={diagramHeadingId}
        >
          <header className="context-action-diagram-ready-header">
            <div className="context-action-diagram-ready-title">
              {detailSection ? (
                <h3 id={diagramHeadingId}>Architecture</h3>
              ) : (
                <p className="context-action-detail-kicker" id={diagramHeadingId}>
                  Architecture
                </p>
              )}
            </div>
            {showGenerationButton ? (
              <div className="context-action-diagram-ready-controls">
                <div className="context-action-diagram-actions">
                  <button
                    type="button"
                    className="context-action-diagram-action"
                    aria-label={failedDiagram ? `Retry ${diagramTypeLabel(failedDiagram)} diagram` : (failed ? "Retry diagram" : undefined)}
                    disabled={busy}
                    onClick={() => requestActionDiagramGeneration(onGenerate, failedDiagram)}
                  >
                    <Sparkles aria-hidden="true" strokeWidth={1.8} />
                    <span>{failed ? "Retry" : "Generate"}</span>
                  </button>
                </div>
              </div>
            ) : null}
          </header>

          <div
            className={[
              "context-action-diagram-viewport",
              generationInProgress ? "is-generating" : null,
              failed ? "is-failed" : null,
              showStream ? "has-stream" : "is-empty"
            ].filter(Boolean).join(" ")}
            aria-live="polite"
          >
            {showStream ? (
              <AgentActivityFeed className="context-action-diagram-stream" collapsible events={visibleStreamEvents} generating={generationInProgress} lines={diagramStreamLines} orbState="shaping" />
            ) : (
              <div className="context-action-diagram-empty">
                <Workflow aria-hidden="true" strokeWidth={1.6} />
              </div>
            )}
          </div>

        </section>
      )
    }

    return (
      <ContextActionEvidenceLists
        modelItems={modelItems}
        workflowItems={workflowItems}
      />
    )
  }

  const diagramTitle = selectedDiagram.title || `${action.label} ${diagramTypeLabel(selectedDiagram)}`
  const hasWorkflowArchitecture = selectedDiagram.diagramType === "workflow"
  const diagramSectionTitle = hasWorkflowArchitecture ? "Architecture" : `${diagramTypeLabel(selectedDiagram)} diagram`
  const sourceUrl = selectedDiagram.embedUrl || selectedDiagram.standaloneUrl
  const showRegenerateButton = Boolean(canGenerate && canRegenerate)
  const regenerateAction = showRegenerateButton ? (
    <button
      type="button"
      className="context-action-diagram-action"
      aria-label={`Regenerate ${diagramSectionTitle}`}
      disabled={busy}
      onClick={() => requestActionDiagramGeneration(onGenerate, selectedDiagram)}
    >
      {busy ? <LoadingOrb state="shaping" /> : <Repeat aria-hidden="true" strokeWidth={1.8} />}
      <span>{busy ? "Generating" : "Regenerate"}</span>
    </button>
  ) : null

  return (
    <section
      className={[
        "context-action-diagram-panel",
        "is-ready",
        detailSection ? "is-detail-section" : null
      ].filter(Boolean).join(" ")}
      aria-labelledby={diagramHeadingId}
    >
      <header className="context-action-diagram-ready-header">
        <div className="context-action-diagram-ready-title">
          {detailSection || !hasWorkflowArchitecture ? (
            <h3 id={diagramHeadingId}>{diagramSectionTitle}</h3>
          ) : (
            <p className="context-action-detail-kicker" id={diagramHeadingId}>
              {diagramSectionTitle}
            </p>
          )}
        </div>
        {availableDiagrams.length > 1 ? (
          <div className="context-action-diagram-ready-controls">
            <div className="context-action-diagram-tabs" role="tablist" aria-label="Diagram type">
              {availableDiagrams.map((diagram) => {
                const active = diagram === selectedDiagram

                return (
                  <button
                    type="button"
                    className={`context-action-diagram-tab ${active ? "is-active" : ""}`.trim()}
                    key={diagram.id || diagram.diagramType}
                    onClick={() => setSelectedDiagramId(diagram.id)}
                    role="tab"
                    aria-selected={active}
                  >
                    {diagramTypeLabel(diagram)}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </header>

      <ContextActionDiagramFrame action={regenerateAction} collapseInitially={collapseReadyDiagram} sourceUrl={sourceUrl} title={diagramTitle} />

    </section>
  )
}

function ContextActionDiagramFrame({ action = null, collapseInitially = false, sourceUrl, title }) {
  const iframeRef = useRef(null)
  const viewportId = useId()
  const [frameHeight, setFrameHeight] = useState(null)
  const [loadedFrameSourceUrl, setLoadedFrameSourceUrl] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const frameLoaded = loadedFrameSourceUrl === sourceUrl
  const collapsed = collapseInitially && !expanded

  const requestFrameHeight = useCallback(() => {
    const frameWindow = iframeRef.current?.contentWindow
    if (!frameWindow) return

    frameWindow.postMessage({ type: contextActionDiagramMeasureRequestType }, "*")
  }, [])

  const handleFrameLoad = useCallback(() => {
    setLoadedFrameSourceUrl(sourceUrl)
    requestFrameHeight()
  }, [requestFrameHeight, sourceUrl])

  useEffect(() => {
    setFrameHeight(null)
    setLoadedFrameSourceUrl(null)
    setExpanded(false)
    setModalOpen(false)
  }, [collapseInitially, sourceUrl])

  useEffect(() => {
    function handleMessage(event) {
      const frameWindow = iframeRef.current?.contentWindow
      if (!frameWindow || event.source !== frameWindow) return
      if (!isContextActionDiagramHeightMessage(event.data)) return

      const nextHeight = normalizedContextActionDiagramHeight(event.data.height)
      if (!nextHeight) return

      setFrameHeight((currentHeight) => {
        if (currentHeight && Math.abs(currentHeight - nextHeight) <= 1) return currentHeight

        return nextHeight
      })
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  useEffect(() => {
    const timers = [80, 360, 900].map((delay) => window.setTimeout(requestFrameHeight, delay))
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [requestFrameHeight, sourceUrl])

  const viewportStyle = frameHeight ? { "--context-action-diagram-frame-height": `${frameHeight}px` } : undefined
  const viewportClassName = [
    "context-action-diagram-viewport",
    frameHeight ? "is-measured" : "",
    frameLoaded ? "is-loaded" : "is-loading",
    collapsed ? "is-collapsed" : "",
    collapseInitially && expanded ? "is-expanded" : ""
  ].filter(Boolean).join(" ")

  return (
    <div
      className={viewportClassName}
      data-context-action-diagram-viewport
      id={viewportId}
      style={viewportStyle}
    >
      <iframe
        className="context-action-diagram-frame"
        data-context-action-diagram-frame
        src={sourceUrl}
        title={title}
        loading="eager"
        onLoad={handleFrameLoad}
        ref={iframeRef}
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-downloads"
      />
      {collapsed ? <div className="prompt-expand-fade context-action-diagram-expand-fade" aria-hidden="true"></div> : null}
      {!collapsed && action ? (
        <div className="context-action-diagram-frame-actions">
          <div className="context-action-diagram-actions">
            {action}
            <button
              type="button"
              className="context-action-diagram-action is-icon"
              aria-label="Expand diagram"
              title="Expand"
              onClick={() => setModalOpen(true)}
            >
              <ZoomIn aria-hidden="true" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      ) : null}
      {modalOpen ? (
        <ContextActionDiagramModal
          initialFrameHeight={frameHeight}
          onClose={() => setModalOpen(false)}
          sourceUrl={sourceUrl}
          title={title}
        />
      ) : null}
      {collapsed ? (
        <button
          type="button"
          className="prompt-expand-button context-action-diagram-expand-button"
          aria-controls={viewportId}
          aria-expanded={expanded}
          aria-label="Expand architectural diagram"
          onClick={() => setExpanded(true)}
        >
          Expand
        </button>
      ) : null}
    </div>
  )
}

function ContextActionDiagramModal({ initialFrameHeight = null, onClose, sourceUrl, title }) {
  const iframeRef = useRef(null)
  const [frameHeight, setFrameHeight] = useState(initialFrameHeight)
  const [loadedFrameSourceUrl, setLoadedFrameSourceUrl] = useState(null)

  const requestFrameHeight = useCallback(() => {
    const frameWindow = iframeRef.current?.contentWindow
    if (!frameWindow) return

    frameWindow.postMessage({ type: contextActionDiagramMeasureRequestType }, "*")
  }, [])

  const handleFrameLoad = useCallback(() => {
    setLoadedFrameSourceUrl(sourceUrl)
    requestFrameHeight()
  }, [requestFrameHeight, sourceUrl])

  useEffect(() => {
    setFrameHeight(initialFrameHeight)
    setLoadedFrameSourceUrl(null)
  }, [initialFrameHeight, sourceUrl])

  useEffect(() => {
    function handleMessage(event) {
      const frameWindow = iframeRef.current?.contentWindow
      if (!frameWindow || event.source !== frameWindow) return
      if (!isContextActionDiagramHeightMessage(event.data)) return

      const nextHeight = normalizedContextActionDiagramHeight(event.data.height)
      if (!nextHeight) return

      setFrameHeight((currentHeight) => {
        if (currentHeight && Math.abs(currentHeight - nextHeight) <= 1) return currentHeight

        return nextHeight
      })
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  useEffect(() => {
    const timers = [80, 360, 900].map((delay) => window.setTimeout(requestFrameHeight, delay))
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [requestFrameHeight, sourceUrl])

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose()
  }

  const frameLoaded = loadedFrameSourceUrl === sourceUrl
  const modalStyle = frameHeight ? { "--context-action-diagram-modal-frame-height": `${frameHeight}px` } : undefined
  const modalClassName = [
    "focus-card",
    "context-action-diagram-modal",
    frameHeight ? "is-measured" : "",
    frameLoaded ? "is-loaded" : "is-loading"
  ].filter(Boolean).join(" ")

  const modal = (
    <div className="stack-modal-backdrop context-action-diagram-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div
        className={modalClassName}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={modalStyle}
      >
        <div className="context-action-diagram-modal-actions">
          <button type="button" className="context-edit-close context-action-diagram-modal-close" aria-label="Close expanded diagram" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <iframe
          className="context-action-diagram-modal-frame"
          src={sourceUrl}
          title={`Expanded ${title}`}
          loading="eager"
          onLoad={handleFrameLoad}
          ref={iframeRef}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-downloads"
        />
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

function isContextActionDiagramHeightMessage(message) {
  return Boolean(
    message &&
      typeof message === "object" &&
      message.type === contextActionDiagramHeightMessageType
  )
}

function normalizedContextActionDiagramHeight(value) {
  const height = Math.ceil(Number(value))
  if (!Number.isFinite(height) || height <= 0) return null

  return Math.min(height, maximumContextActionDiagramFrameHeight)
}

function ContextActionEvidenceLists({ compact = false, modelItems = [], workflowItems = [] }) {
  const modelHeadingId = compact ? "context-action-evidence-models-heading" : "context-action-models-heading"
  const workflowHeadingId = compact ? "context-action-evidence-workflow-heading" : "context-action-workflow-heading"

  return (
    <div className={`context-action-detail-support ${compact ? "is-compact" : ""}`.trim()}>
      <section className="context-action-detail-list" aria-labelledby={modelHeadingId}>
        <div className="context-action-detail-list-heading">
          <Database aria-hidden="true" strokeWidth={1.8} />
          <h3 id={modelHeadingId}>Data models</h3>
        </div>
        {modelItems.length ? (
          <div className="context-action-model-list">
            {modelItems.map((model, index) => (
              <article className="context-action-model-item" key={model.path || model.name}>
                <span className={`context-object-icon is-${nodePalette[index % nodePalette.length]}`} aria-hidden="true">
                  {objectIcon(model.pattern || "database")}
                </span>
                <div>
                  <strong>{model.label}</strong>
                  <span>{model.pattern || "Data model"}</span>
                  {model.role ? <p>{model.role}</p> : null}
                  <code>{model.path}</code>
                </div>
              </article>
            ))}
          </div>
        ) : <ContextEmptyState label="No code data models mapped yet." />}
      </section>

      <section className="context-action-detail-list" aria-labelledby={workflowHeadingId}>
        <div className="context-action-detail-list-heading">
          <Server aria-hidden="true" strokeWidth={1.8} />
          <h3 id={workflowHeadingId}>Workflow code</h3>
        </div>
        {workflowItems.length ? (
          <div className="context-action-workflow-list">
            {workflowItems.map((item) => (
              <article className="context-action-workflow-item" key={item.path || item.name}>
                <span>{item.pattern || "Workflow code"}</span>
                <strong>{item.label}</strong>
                {item.role ? <p>{item.role}</p> : null}
                <code>{item.path}</code>
              </article>
            ))}
          </div>
        ) : <ContextEmptyState label="No workflow code mapped yet." />}
      </section>
    </div>
  )
}

function selectedContextActionFrameItem({
  action,
  frameIndex,
  frames,
  framesEditable,
  onEditFrame,
  plannedFrame,
  storyboard,
  visibleIndex
}) {
  const frame = frames[frameIndex]
  if (!frame) return null

  return {
    action,
    displayFrameIndex: visibleIndex,
    frame,
    frameIndex,
    onEdit: framesEditable ? () => onEditFrame?.(storyboard, frameIndex, { displayFrameIndex: visibleIndex }) : null,
    plannedFrame
  }
}

function contextActionProductShotLabel(frame = {}) {
  return frame.title || frame.description || null
}

function ContextActionFrameModal({ frameItem, frameTotal = 0, onClose, onNext, onPrevious }) {
  const titleId = useId()
  const descriptionId = useId()
  const imageRef = useRef(null)
  const [imageLoading, setImageLoading] = useState(false)
  const imageSrc = frameItem?.frame?.url || null

  useEffect(() => {
    if (!frameItem) return undefined

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose()
        return
      }

      if (event.key === "ArrowLeft" && onPrevious) {
        event.preventDefault()
        setImageLoading(true)
        onPrevious()
        return
      }

      if (event.key === "ArrowRight" && onNext) {
        event.preventDefault()
        setImageLoading(true)
        onNext()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [frameItem, onClose, onNext, onPrevious])

  useEffect(() => {
    if (!imageSrc) {
      setImageLoading(false)
      return
    }

    const image = imageRef.current
    setImageLoading(!(image?.complete))
  }, [imageSrc])

  if (!frameItem) return null

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose()
  }

  function handleEdit() {
    const editFrame = frameItem.onEdit
    onClose()
    editFrame?.()
  }

  function handleImageLoad() {
    setImageLoading(false)
  }

  function handleImageError() {
    setImageLoading(false)
  }

  function handlePreviousFrame() {
    if (!onPrevious) return

    setImageLoading(true)
    onPrevious()
  }

  function handleNextFrame() {
    if (!onNext) return

    setImageLoading(true)
    onNext()
  }

  const displayFrameIndex = frameItem.displayFrameIndex ?? frameItem.frameIndex
  const title = frameItem.plannedFrame?.title || `${frameItem.action?.label || "Storyboard"} frame ${displayFrameIndex + 1}`
  const description = frameItem.plannedFrame?.description
  const showNavigation = frameTotal > 1
  const modal = (
    <div className="stack-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div
        className="focus-card stack-selection-modal context-action-frame-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <div className="context-action-frame-modal-actions">
          {frameItem.onEdit ? (
            <EditContextButton
              className="context-action-frame-modal-edit"
              label={`Edit ${title}`}
              onClick={handleEdit}
            />
          ) : null}
          <button type="button" className="context-edit-close context-action-frame-modal-close" aria-label="Close image preview" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <figure className="context-action-frame-modal-figure">
          <div className="context-action-frame-modal-image">
            <img
              ref={imageRef}
              src={frameItem.frame.url}
              alt={frameItem.frame.alt || title}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            <div
              className={`context-action-frame-modal-loading ${imageLoading ? "is-loading" : ""}`.trim()}
              role={imageLoading ? "status" : undefined}
              aria-label={imageLoading ? "Loading frame image" : undefined}
              aria-hidden={imageLoading ? undefined : "true"}
            >
              <LoaderCircle aria-hidden="true" strokeWidth={1.8} />
            </div>
            {showNavigation ? (
              <div className="context-action-frame-modal-image-navigation" aria-label="Storyboard frame navigation">
                <button
                  type="button"
                  className="context-action-frame-modal-image-nav is-previous"
                  aria-label="Previous storyboard frame"
                  title="Previous frame"
                  disabled={!onPrevious}
                  onClick={onPrevious ? handlePreviousFrame : undefined}
                >
                  <ArrowLeft size={19} aria-hidden="true" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  className="context-action-frame-modal-image-nav is-next"
                  aria-label="Next storyboard frame"
                  title="Next frame"
                  disabled={!onNext}
                  onClick={onNext ? handleNextFrame : undefined}
                >
                  <ArrowRight size={19} aria-hidden="true" strokeWidth={1.8} />
                </button>
              </div>
            ) : null}
          </div>
          <figcaption className="context-action-frame-modal-copy">
            {frameItem.caption ? <span className="context-storyboard-frame-caption">{frameItem.caption}</span> : null}
            <strong id={titleId}>{title}</strong>
            {description ? <span id={descriptionId}>{description}</span> : null}
          </figcaption>
        </figure>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

function storyboardFramePlanPayload(frame = {}) {
  return {
    title: frame.title || "",
    description: frame.description || "",
    detail_prompt: storyboardFrameDetailPrompt(frame)
  }
}

function storyboardFrameContentsChanged(previousFrame = {}, nextFrame = {}) {
  return storyboardFrameText(previousFrame.title) !== storyboardFrameText(nextFrame.title) ||
    storyboardFrameText(previousFrame.description) !== storyboardFrameText(nextFrame.description) ||
    storyboardFrameText(storyboardFrameDetailPrompt(previousFrame)) !== storyboardFrameText(storyboardFrameDetailPrompt(nextFrame))
}

function storyboardFrameDetailPrompt(frame = {}) {
  return frame.detailPrompt || frame.detail_prompt || frame.imagePrompt || frame.image_prompt || frame.visualPrompt || frame.visual_prompt || frame.description || ""
}

function storyboardFrameText(value) {
  return String(value ?? "").trim().replace(/\s+/g, " ")
}

function storyboardFrameRegenerationKey(actionKey, frameIndex) {
  return `${actionKey}:${frameIndex}`
}

function storyboardFrameRegenerationActionKey(regenerationKey) {
  return String(regenerationKey || "").split(":")[0]
}

function storyboardPayloadGenerating(storyboards = [], actionKey) {
  return storyboards.some((storyboard) => storyboard?.actionKey === actionKey && storyboardGenerationActive(storyboard))
}

function mergeUpdatedStoryboard(storyboards = [], updatedStoryboard) {
  if (!updatedStoryboard?.actionKey) return storyboards

  let replaced = false
  const nextStoryboards = storyboards.map((storyboard) => {
    if (storyboard.actionKey !== updatedStoryboard.actionKey) return storyboard

    replaced = true
    return updatedStoryboard
  })

  return replaced ? nextStoryboards : [ ...nextStoryboards, updatedStoryboard ]
}

function StoryboardFrameCopy({ caption, className = "context-storyboard-frame-copy", description, editLabel, onEdit, title }) {
  const editable = typeof onEdit === "function"
  if (!caption && !title && !description && !editable) return null

  const CopyElement = className === "context-action-detail-frame-copy" ? "figcaption" : "div"

  return (
    <CopyElement className={`${className} ${editable ? "is-editable" : ""}`.trim()}>
      {caption ? <span className="context-storyboard-frame-caption">{caption}</span> : null}
      {title ? <strong>{title}</strong> : null}
      {description ? <span>{description}</span> : null}
      {editable ? (
        <EditContextButton
          className="context-frame-edit-button"
          label={editLabel || "Edit frame text"}
          onClick={onEdit}
        />
      ) : null}
    </CopyElement>
  )
}

function ContextStoryboardPlaceholder({ status }) {
  return (
    <div className={`context-storyboard-placeholder is-${status}`}>
      {status === "generating" ? (
        <LoadingOrb size={64} state="shaping" />
      ) : status === "failed" ? (
        <CircleX aria-hidden="true" strokeWidth={1.8} />
      ) : (
        <Image aria-hidden="true" strokeWidth={1.8} />
      )}
      <span>{storyboardPlaceholderLabel(status)}</span>
    </div>
  )
}

function storyboardStatus(storyboard) {
  if (!storyboard) return "idle"
  if (storyboardReady(storyboard)) return "ready"
  return storyboard.status || "idle"
}

function storyboardReady(storyboard) {
  return storyboard?.status === "ready" && storyboardFramesComplete(storyboard)
}

function actionStoryboardsReady(actions = [], storyboardsByKey = new Map()) {
  const items = limitedList(actions)
  return Boolean(items.length) && items.every((action) => {
    return storyboardReady(storyboardsByKey.get(contextActionKey(action)))
  })
}

export function actionPersonaUsers(action = {}, users = []) {
  const items = limitedList(users)
  if (!items.length) return []

  const usersByReference = personaUsersByReference(items)
  const explicitReferences = actionPersonaReferences(action)
  const explicitMatches = usersForPersonaReferences(explicitReferences, usersByReference)
  if (explicitMatches.length) return explicitMatches

  return inferredActionPersonaUsers(action, items)
}

function personaUsersByReference(users = []) {
  return users.reduce((index, user) => {
    personaReferenceVariants(user.key).forEach((reference) => {
      if (!index.has(reference)) index.set(reference, user)
    })
    personaReferenceVariants(personaFullDisplayName(user)).forEach((reference) => {
      if (!index.has(reference)) index.set(reference, user)
    })
    personaReferenceVariants(personaDisplayName(user)).forEach((reference) => {
      if (!index.has(reference)) index.set(reference, user)
    })
    personaReferenceVariants(user.name).forEach((reference) => {
      if (!index.has(reference)) index.set(reference, user)
    })

    return index
  }, new Map())
}

function usersForPersonaReferences(references = [], usersByReference = new Map()) {
  const seen = new Set()

  return references.flatMap((reference) => {
    const user = usersByReference.get(reference)
    if (!user) return []

    const key = user.key || user.name
    if (seen.has(key)) return []

    seen.add(key)
    return [ user ]
  })
}

function actionPersonaReferences(action = {}) {
  return [
    action.userKeys,
    action.user_keys,
    action.personaKeys,
    action.persona_keys,
    action.primaryUserKeys,
    action.primary_user_keys,
    action.users,
    action.primaryUsers,
    action.primary_users,
    action.personas
  ].flatMap(personaReferenceValues).flatMap(personaReferenceVariants)
}

function personaReferenceValues(value) {
  if (Array.isArray(value)) return value.flatMap(personaReferenceValues)
  if (value && typeof value === "object") {
    return [
      value.key,
      value.id,
      value.name,
      value.label
    ].filter(Boolean)
  }

  return String(value ?? "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function inferredActionPersonaUsers(action = {}, users = []) {
  const actionText = searchableContextText([
    action.key,
    action.label,
    action.name,
    action.description,
    action.icon,
    codeEnablerSearchText(action.codeEnablers)
  ])
  if (!actionText) return []

  return users.filter((user) => personaMatchesActionText(user, actionText))
}

function personaMatchesActionText(user = {}, actionText = "") {
  const directReferences = [
    ...personaReferenceVariants(user.key),
    ...personaReferenceVariants(user.name),
    ...personaReferenceVariants(personaFullDisplayName(user)),
    ...personaReferenceVariants(personaDisplayName(user))
  ]
  if (directReferences.some((reference) => reference && actionText.includes(reference))) return true

  const actionTokens = significantPersonaTokens(actionText)
  if (!actionTokens.size) return false

  const userTokens = significantPersonaTokens(searchableContextText([
    personaFullDisplayName(user),
    personaDisplayName(user),
    user.name,
    user.description,
    user.goals,
    user.criticalNeeds,
    user.critical_needs
  ]))
  let matchCount = 0
  actionTokens.forEach((token) => {
    if (userTokens.has(token)) matchCount += 1
  })

  return matchCount >= 2
}

function codeEnablerSearchText(codeEnablers = {}) {
  return [
    codeEnablers?.dataModels,
    codeEnablers?.workflowCode,
    codeEnablers?.services
  ].flatMap((items) => limitedList(items)).flatMap((item) => [
    item?.name,
    item?.path,
    item?.pattern,
    item?.role,
    item?.description
  ])
}

function personaReferenceVariants(value) {
  const key = searchableContextText(value)
  if (!key) return []

  const variants = new Set([ key ])
  if (key.endsWith("ies") && key.length > 4) variants.add(`${key.slice(0, -3)}y`)
  if (key.endsWith("es") && key.length > 3) variants.add(key.slice(0, -2))
  if (key.endsWith("s") && key.length > 3) variants.add(key.slice(0, -1))

  return Array.from(variants).filter((variant) => variant.length > 2)
}

function significantPersonaTokens(value) {
  return new Set(searchableContextText(value).split("-").filter((token) => {
    return token.length > 3 && !commonPersonaTokenStopWords.has(token)
  }))
}

function searchableContextText(value) {
  const text = Array.isArray(value) ? value.flatMap((item) => {
    if (item && typeof item === "object") return Object.values(item)
    return item
  }).join(" ") : String(value ?? "")

  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
}

const commonPersonaTokenStopWords = new Set([
  "action",
  "actions",
  "clear",
  "code",
  "context",
  "current",
  "data",
  "easy",
  "fast",
  "fewer",
  "from",
  "goal",
  "goals",
  "info",
  "journey",
  "needs",
  "product",
  "real",
  "right",
  "service",
  "state",
  "status",
  "time",
  "track",
  "update",
  "updates",
  "user",
  "users",
  "workflow",
  "workflows"
])

function personaText(persona = {}, ...keys) {
  for (const key of keys) {
    const value = persona?.[key]
    if (typeof value === "string" && value.trim()) return value.trim()
  }

  return ""
}

function personaArray(persona = {}, ...keys) {
  return keys.flatMap((key) => personaListValues(persona?.[key])).filter(Boolean)
}

function personaListValues(value) {
  if (Array.isArray(value)) return value.flatMap(personaListValues)
  if (value && typeof value === "object") {
    const label = value.label || value.name || value.key
    const detail = value.value || value.description || value.relationship
    return [ [ label, detail ].filter(Boolean).join(": ") ].filter(Boolean)
  }
  if (typeof value === "string") {
    return value
      .split(/\n+/)
      .map((item) => item.trim().replace(/^[-*]\s*/, ""))
      .filter(Boolean)
  }

  return String(value ?? "").trim() ? [ String(value).trim() ] : []
}

function personaHeadlineQuote(persona = {}) {
  return personaText(persona, "headlineQuote", "headline_quote", "quote")
}

export function personaDisplayName(persona = {}) {
  return personaText(persona, "name", "label") ||
    "Persona"
}

function personaFullDisplayName(persona = {}) {
  return personaText(persona, "name", "label") ||
    "Persona"
}

function readablePersonaLabel(value = "") {
  const text = String(value || "").trim()
  if (!text) return ""

  const letters = text.replace(/[^a-z]/gi, "")
  if (!letters || letters !== letters.toUpperCase()) return text

  return text
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
    .replace(/\bAi\b/g, "AI")
    .replace(/\bApi\b/g, "API")
    .replace(/\bLlm\b/g, "LLM")
    .replace(/\bUi\b/g, "UI")
    .replace(/\bUx\b/g, "UX")
}

function personaRoleName(persona = {}) {
  const displayName = personaDisplayName(persona)
  const roleName = personaText(persona, "name", "label")
  if (!roleName || roleName === displayName) return ""

  return readablePersonaLabel(roleName)
}

function personaArchetypeName(persona = {}) {
  return readablePersonaLabel(personaText(persona, "archetype", "personaType", "persona_type"))
}

function personaGoalItems(persona = {}) {
  return uniqueTextItems([
    personaText(persona, "primaryGoal", "primary_goal"),
    personaArray(persona, "goals"),
    personaArray(persona, "secondaryGoals", "secondary_goals")
  ])
}

export function personaCardEssence(persona = {}) {
  return firstUniquePersonaItem([
    personaText(persona, "primaryGoal", "primary_goal"),
    personaArray(persona, "goals"),
    personaHeadlineQuote(persona),
    personaText(persona, "howToSpot", "how_to_spot", "description"),
    personaArray(persona, "careAbouts", "care_abouts"),
    personaArray(persona, "criticalNeeds", "critical_needs"),
    personaArray(persona, "decisionCriteria", "decision_criteria"),
    personaArray(persona, "secondaryGoals", "secondary_goals")
  ])
}

function firstUniquePersonaItem(items = []) {
  return uniqueTextItems(items)[0] || ""
}

function uniqueTextItems(items = []) {
  const seen = new Set()

  return items.flatMap(personaListValues).filter((item) => {
    const key = searchableContextText(item)
    if (!key || seen.has(key)) return false

    seen.add(key)
    return true
  })
}

function personaFactItems(persona = {}) {
  const facts = persona?.aboutFacts || persona?.about_facts || persona?.about || []

  return Array.isArray(facts) ? facts.filter(Boolean).flatMap((fact) => {
    if (fact && typeof fact === "object") {
      const label = fact.label || fact.name || fact.key
      const value = fact.value || fact.description || fact.text
      return value ? [{ label, value }] : []
    }

    return String(fact).trim() ? [{ value: String(fact).trim() }] : []
  }) : []
}

function personaRelationshipItems(persona = {}, users = []) {
  const usersByKey = new Map(limitedList(users).map((user) => [ contextPersonaKey(user), user ]))
  const relationships = persona?.relationships || persona?.personaRelationships || persona?.persona_relationships || []

  return Array.isArray(relationships) ? relationships.filter(Boolean).flatMap((relationship) => {
    if (relationship && typeof relationship === "object") {
      const personaKey = relationship.personaKey || relationship.persona_key || relationship.targetKey || relationship.key
      const relatedUser = usersByKey.get(personaKey)
      return [{
        personaKey,
        name: relationship.name || relationship.label || relatedUser?.name,
        relationship: relationship.relationship || relationship.description || relationship.value
      }].filter((item) => item.name || item.relationship)
    }

    return String(relationship).trim() ? [{ relationship: String(relationship).trim() }] : []
  }) : []
}

function personaRelatedActions(persona = {}, actions = []) {
  const personaKey = contextPersonaKey(persona)

  return limitedList(actions).filter((action) => {
    return actionPersonaUsers(action, [ persona ]).some((user) => contextPersonaKey(user) === personaKey)
  })
}

export function personaRelatedIssues(persona = {}, issues = []) {
  const personaKey = contextPersonaKey(persona)

  return limitedList(issues).filter((issue) => {
    const references = issue?.personaKeys || issue?.persona_keys || []
    return personaReferencesInclude(references, personaKey)
  })
}

function personaRelatedConceptMoves(persona = {}, concept = null) {
  const personaKey = contextPersonaKey(persona)
  const concepts = Array.isArray(concept?.concepts?.items) ? concept.concepts.items : []

  return concepts.flatMap((conceptItem) => {
    return limitedList(conceptItem?.nextMoves).filter((move) => {
      return personaReferencesInclude(move?.personaKey || move?.persona_key, personaKey)
    }).map((move) => ({
      ...move,
      conceptKey: conceptItem.key,
      conceptTitle: conceptItem.displayTitle || conceptItem.title,
      detailPath: conceptItem.detailPath
    }))
  }).slice(0, 6)
}

function personaReferencesInclude(references, personaKey) {
  const targetReferences = new Set(personaReferenceVariants(personaKey))

  return personaReferenceValues(references).some((reference) => {
    return personaReferenceVariants(reference).some((variant) => targetReferences.has(variant))
  })
}

function storyboardFramesComplete(storyboard) {
  const frames = Array.isArray(storyboard?.frames) ? storyboard.frames : []
  const framePlan = Array.isArray(storyboard?.framePlan) ? storyboard.framePlan : []
  const uiFrames = Array.isArray(storyboard?.uiFrames) ? storyboard.uiFrames : []
  const uiFramePlan = Array.isArray(storyboard?.uiFramePlan) ? storyboard.uiFramePlan : []

  return framePlan.length >= minimumStoryboardFrameCount &&
    frames.length >= framePlan.length &&
    uiFramePlan.length === framePlan.length &&
    uiFrames.length >= uiFramePlan.length
}

function storyboardFrameEditingAvailable(storyboard) {
  return Boolean(storyboard?.updatePath && storyboardReady(storyboard))
}

function storyboardFrameIndexes(storyboard, frames = []) {
  const plannedCount = Array.isArray(storyboard?.framePlan) ? storyboard.framePlan.length : 0
  const frameCount = Math.max(plannedCount, frames.length, minimumStoryboardFrameCount)
  const cappedFrameCount = Math.min(frameCount, maximumStoryboardFrameCount)

  return Array.from({ length: cappedFrameCount }, (_, index) => index)
}

function storyboardStatusLabel(storyboard, options) {
  const status = storyboardStatus(storyboard, options)

  if (status === "ready") return "Ready"
  if (status === "generating") return "Generating"
  if (status === "failed") return "Failed"
  return "Not generated"
}

function storyboardPlaceholderLabel(status) {
  if (status === "generating") return "Generating frames"
  if (status === "failed") return "Generation failed"
  return "Frames not generated"
}

export function storyboardContextSignatureFor(context) {
  if (!context) return ""

  return JSON.stringify(contextSemanticSignatureValue({
    actions: limitedList(context.actions),
    outcomes: context.outcomes || [],
    productSummary: context.productSummary || context.product_summary || null,
    system: context.system || context.keyObjectsAndRelationships || context.key_objects_and_relationships || null,
    users: context.users || []
  }))
}

function contextSemanticSignatureValue(value) {
  if (Array.isArray(value)) return value.map(contextSemanticSignatureValue)
  if (!value || typeof value !== "object") return value

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !contextSemanticSignatureIgnoredKey(key))
      .map(([key, nestedValue]) => [key, contextSemanticSignatureValue(nestedValue)])
  )
}

function contextSemanticSignatureIgnoredKey(key) {
  return contextSemanticSignatureIgnoredKeys.has(key) ||
    contextSemanticSignatureIgnoredKeys.has(underscoreContextKey(key))
}

function underscoreContextKey(key) {
  return String(key || "").replace(/([A-Z])/g, "_$1").toLowerCase()
}

function storyboardGenerationTargetKeys(context, actionKey) {
  if (actionKey) return [ actionKey ]

  return limitedList(context?.actions).map((action) => contextActionKey(action)).filter(Boolean)
}

export function mergeStoryboardsWithPendingGeneration(nextStoryboards = [], currentStoryboards = [], pendingActionKeys = new Set()) {
  const currentStoryboardsByActionKey = new Map(
    currentStoryboards.map((storyboard) => [ storyboard.actionKey, storyboard ])
  )
  const mergedStoryboards = nextStoryboards.map((storyboard) => {
    const currentStoryboard = currentStoryboardsByActionKey.get(storyboard.actionKey)

    return storyboardRefreshResponseStale(storyboard, currentStoryboard) ? currentStoryboard : storyboard
  })
  if (!pendingActionKeys?.size) return mergedStoryboards

  const nextStoryboardsByActionKey = new Map(
    mergedStoryboards.map((storyboard) => [ storyboard.actionKey, storyboard ])
  )

  currentStoryboards.forEach((storyboard) => {
    if (!storyboardGenerationActive(storyboard)) return
    if (!pendingActionKeys.has(storyboard.actionKey)) return
    if (nextStoryboardsByActionKey.has(storyboard.actionKey)) return

    mergedStoryboards.push(storyboard)
  })

  return mergedStoryboards
}

function storyboardRefreshResponseStale(nextStoryboard, currentStoryboard) {
  const currentRefreshAttempt = currentStoryboard?.refreshAttempt
  if (!activeStoryboardGenerationStatuses.has(currentRefreshAttempt?.status)) return false
  if (storyboardGenerationActive(nextStoryboard)) return false

  const currentStartedAt = currentRefreshAttempt.startedAt
  const nextStartedAt = nextStoryboard?.refreshAttempt?.startedAt || nextStoryboard?.startedAt

  return Boolean(currentStartedAt) && nextStartedAt !== currentStartedAt
}

export function storyboardGenerationActive(storyboard) {
  return activeStoryboardGenerationStatuses.has(storyboard?.status) ||
    activeStoryboardGenerationStatuses.has(storyboard?.refreshAttempt?.status)
}

export function contextActionKey(action = {}) {
  return action.key || String(action.label || action.name || "action").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
}

export function contextPersonaKey(persona = {}) {
  return persona.key || String(persona.name || "persona").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
}

function actionKeyForStoryboardRegeneration(editor, attributes = {}, previousContext, nextContext) {
  if (editor?.section !== "actions" || !nextContext) return null
  if (editor.key) return editor.key

  const previousKeys = new Set(limitedList(previousContext?.actions).map((action) => contextActionKey(action)))
  const nextActions = limitedList(nextContext.actions)
  const createdAction = nextActions.find((action) => !previousKeys.has(contextActionKey(action)))
  if (createdAction) return contextActionKey(createdAction)

  const savedLabel = attributes.label || attributes.title || attributes.name
  const matchedAction = nextActions.find((action) => action.key === attributes.key || action.label === savedLabel)
  return matchedAction ? contextActionKey(matchedAction) : null
}

function pathWithQueryParam(path, key, value) {
  if (!path || !value) return path

  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`
}

function SystemSection({ canEdit = false, onAddObject, onAddRelationship, onEdit, onMoveObject, system = {} }) {
  const graph = useMemo(() => relationshipGraph(system), [system])
  const objects = relationshipObjects(system.objects)

  if (!graph.nodes.length && !canEdit) return null

  return (
    <ContextPanel
      action={canEdit ? (
        <ContextPanelActions>
          <AddSystemItemMenu
            canAddRelationship={objects.length >= 2}
            onAddObject={onAddObject}
            onAddRelationship={onAddRelationship}
          />
        </ContextPanelActions>
      ) : null}
      id="context-system"
      kicker="What"
      title="Objects & Relationships"
    >
      {graph.nodes.length ? (
        <RelationshipGraph
          canEdit={canEdit}
          graph={graph}
          onEdit={onEdit}
          onMoveObject={onMoveObject}
        />
      ) : <ContextEmptyState label="No key objects yet." />}
    </ContextPanel>
  )
}

function RelationshipGraph({ canEdit, graph, onEdit, onMoveObject }) {
  if (typeof window === "undefined") {
    return <StaticRelationshipGraph canEdit={canEdit} graph={graph} onEdit={onEdit} />
  }

  return (
    <InteractiveRelationshipGraph
      canEdit={canEdit}
      graph={graph}
      onEdit={onEdit}
      onMoveObject={onMoveObject}
    />
  )
}

function InteractiveRelationshipGraph({ canEdit, graph, onEdit, onMoveObject }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(() => contextFlowNodes(graph, canEdit))
  const [edges, setEdges, onEdgesChange] = useEdgesState(() => contextFlowEdges(graph, canEdit))
  const nodeSignature = contextGraphNodeSignature(graph, canEdit)
  const edgeSignature = contextGraphEdgeSignature(graph, canEdit)
  const appliedNodeSignatureRef = useRef(nodeSignature)
  const onSafeNodesChange = useCallback((changes) => {
    onNodesChange(graphChangesWithoutLocalDeletes(changes))
  }, [onNodesChange])
  const onSafeEdgesChange = useCallback((changes) => {
    onEdgesChange(graphChangesWithoutLocalDeletes(changes))
  }, [onEdgesChange])

  useEffect(() => {
    if (appliedNodeSignatureRef.current === nodeSignature) return

    appliedNodeSignatureRef.current = nodeSignature
    setNodes(contextFlowNodes(graph, canEdit))
  }, [canEdit, graph, nodeSignature, setNodes])

  useEffect(() => {
    const nextEdges = contextFlowEdges(contextGraphWithFlowNodePositions(graph, nodes), canEdit)
    setEdges((currentEdges) => contextFlowEdgesEqual(currentEdges, nextEdges) ? currentEdges : nextEdges)
  }, [canEdit, edgeSignature, graph, nodes, setEdges])

  function editObject(node) {
    if (!canEdit) return

    onEdit(systemObjectEditorPayload(node.data.object))
  }

  function editRelationship(edge) {
    if (!canEdit || !edge.data?.relationship) return

    onEdit(systemRelationshipEditorPayload(edge.data.relationship))
  }

  return (
    <div className="context-relationship-scroll">
      <div className="context-relationship-flow-shell">
        <ReactFlow
          className="context-relationship-flow"
          colorMode="dark"
          edges={edges}
          elementsSelectable={canEdit}
          fitView
          fitViewOptions={relationshipFlowFitViewOptions}
          maxZoom={1.55}
          minZoom={contextFlowMinimumZoom}
          nodes={nodes}
          nodesConnectable={false}
          nodesDraggable={canEdit}
          nodesFocusable={canEdit}
          nodeOrigin={contextFlowNodeOrigin}
          nodeTypes={contextFlowNodeTypes}
          onEdgeClick={canEdit ? (_event, edge) => editRelationship(edge) : undefined}
          onEdgesChange={onSafeEdgesChange}
          onNodeClick={canEdit ? (_event, node) => editObject(node) : undefined}
          onNodeDragStop={canEdit ? (_event, node) => {
            onMoveObject?.(node.data.object, roundedFlowPosition(node.position))
          } : undefined}
          onNodesChange={onSafeNodesChange}
          panOnDrag
          proOptions={contextFlowProOptions}
          zoomOnDoubleClick
          zoomOnPinch
          zoomOnScroll={false}
          {...contextFlowDeletionProtection}
        >
          <Background color="rgba(255,255,255,0.13)" gap={22} size={1} />
          <Controls position="bottom-right" showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  )
}

function StaticRelationshipGraph({ canEdit, graph, onEdit }) {
  return (
    <div className="context-relationship-scroll">
      <div
        className="context-relationship-graph"
        role={canEdit ? undefined : "img"}
        aria-label="Objects and relationships"
        style={{
          height: `${graph.height}px`,
          minWidth: `${graph.width}px`,
          width: `${graph.width}px`
        }}
      >
        <svg className="context-relationship-edges" viewBox={`0 0 ${graph.width} ${graph.height}`} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <marker id="context-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 8 4 L 0 8 z" />
            </marker>
          </defs>
          {graph.edges.map((edge) => (
            <line
              key={`${relationshipKeyFor(edge)}-${edge.sourceIndex}-line`}
              x1={edge.from.x}
              y1={edge.from.y}
              x2={edge.to.x}
              y2={edge.to.y}
            />
          ))}
        </svg>
        {graph.edges.map((edge) => {
          const displayLabel = relationshipDisplayLabel(edge)
          if (!canEdit && !displayLabel) return null

          return (
            <button
              type="button"
              className={`context-edge-label ${canEdit ? "is-editable" : ""}`.trim()}
              disabled={!canEdit}
              aria-label={canEdit ? `Edit ${relationshipTitleFor(edge)} relationship` : undefined}
              key={`${relationshipKeyFor(edge)}-${edge.sourceIndex}-label`}
              onClick={canEdit ? () => onEdit(systemRelationshipEditorPayload(edge)) : undefined}
              style={{
                left: `${(edge.from.x + edge.to.x) / 2}px`,
                top: `${(edge.from.y + edge.to.y) / 2}px`
              }}
            >
              {displayLabel || "Unlabeled"}
            </button>
          )
        })}
        {graph.nodes.map((node, index) => {
          const content = (
            <>
              <span className={`context-object-icon is-${nodePalette[index % nodePalette.length]}`} aria-hidden="true">
                {objectIcon(node.kind)}
              </span>
              <span>{node.label}</span>
            </>
          )
          const style = {
            left: `${node.x}px`,
            top: `${node.y}px`
          }

          return canEdit ? (
            <button
              type="button"
              className="context-object-node is-editable"
              aria-label={`Edit ${node.label}`}
              key={node.id}
              onClick={() => onEdit(systemObjectEditorPayload(node))}
              style={style}
            >
              {content}
            </button>
          ) : (
            <article className="context-object-node" key={node.id} style={style}>
              {content}
            </article>
          )
        })}
      </div>
    </div>
  )
}

function ContextObjectFlowNode({ data }) {
  const kind = objectKindLabel(data.kind)

  return (
    <div className={`context-object-flow-node ${data.editable ? "is-editable" : ""}`.trim()}>
      <ContextFlowHandles type="target" />
      <header className="context-object-flow-header">
        <span className={`context-object-icon is-${data.palette}`} aria-hidden="true">
          {objectIcon(data.kind)}
        </span>
        <span className="context-object-flow-identity">
          <span className="context-object-flow-label">{data.label}</span>
          {kind ? <span className="context-object-flow-meta">{kind}</span> : null}
        </span>
      </header>
      {data.description ? <p className="context-object-flow-description">{data.description}</p> : null}
      <ContextFlowHandles type="source" />
    </div>
  )
}

const contextFlowHandleSides = [
  { id: "top", position: Position.Top },
  { id: "right", position: Position.Right },
  { id: "bottom", position: Position.Bottom },
  { id: "left", position: Position.Left }
]

function ContextFlowHandles({ type }) {
  return contextFlowHandleSides.map((side) => (
    <Handle
      id={`${type}-${side.id}`}
      key={`${type}-${side.id}`}
      type={type}
      position={side.position}
      className="context-flow-handle"
    />
  ))
}

const contextFlowNodeTypes = {
  contextObject: ContextObjectFlowNode
}

function ContextPanel({ action, children, id, kicker, title }) {
  return (
    <section className="context-panel" id={id}>
      {kicker ? <span className="context-band-kicker">{kicker}</span> : null}
      <div className="context-panel-heading">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  )
}

function ContextPanelActions({ children }) {
  return <div className="context-panel-actions">{children}</div>
}

function AddContextButton({ label, onClick }) {
  return (
    <button type="button" className="context-add-button" onClick={onClick}>
      <Plus size={13} aria-hidden="true" />
      <span>{label}</span>
    </button>
  )
}

function AddSystemItemMenu({ canAddRelationship = false, onAddObject, onAddRelationship }) {
  const [open, setOpen] = useState(false)
  const menuRef = useDismissiblePopover(open, () => setOpen(false))

  function choose(callback) {
    setOpen(false)
    callback?.()
  }

  return (
    <div className="context-add-menu" ref={menuRef}>
      <button
        type="button"
        className="context-add-button context-add-menu-trigger"
        aria-label="Add key object or relationship"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
      >
        <Plus size={13} aria-hidden="true" />
        <span>Add</span>
        <ChevronDown className="context-add-menu-chevron" aria-hidden="true" strokeWidth={1.8} />
      </button>
      {open ? (
        <div className="context-add-menu-popover" role="menu" aria-label="Add key object or relationship">
          <button type="button" role="menuitem" onClick={() => choose(onAddObject)}>
            <Box aria-hidden="true" strokeWidth={1.8} />
            <span>Object</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!canAddRelationship}
            title={canAddRelationship ? undefined : "Add at least two objects before creating a relationship."}
            onClick={() => choose(onAddRelationship)}
          >
            <Link aria-hidden="true" strokeWidth={1.8} />
            <span>Relationship</span>
          </button>
        </div>
      ) : null}
    </div>
  )
}

function EditContextButton({ className = "", label, onClick }) {
  return (
    <button
      type="button"
      className={`context-edit-button ${className}`.trim()}
      aria-label={label}
      onClick={onClick}
    >
      <Pencil aria-hidden="true" strokeWidth={1.8} />
    </button>
  )
}

function ContextEmptyState({ label }) {
  return <p className="context-empty-state">{label}</p>
}

function ContextSaveMessage({ error }) {
  if (!error) return null

  return <p className="context-save-message is-error" role="alert">{error}</p>
}

function ContextEditModal({ context, error, item, onClose, onDelete, onSave, saveState }) {
  const firstFieldRef = useRef(null)
  const [draft, setDraft] = useState(() => draftFromContextItem(item))

  useEffect(() => {
    setDraft(draftFromContextItem(item))
    window.setTimeout(() => firstFieldRef.current?.focus(), 0)
  }, [item])

  useEffect(() => {
    if (!item) return undefined

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [item, onClose])

  if (!item) return null

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose()
  }

  function handleSubmit(event) {
    event.preventDefault()
    onSave(payloadFromContextDraft(item, draft))
  }

  function handleDelete() {
    if (typeof window !== "undefined" && !window.confirm(`Delete ${item.title || "this context item"}?`)) return

    onDelete(payloadFromContextDraft(item, draft))
  }

  const canDelete = deletableContextItem(item)

  const modal = (
    <div className="stack-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div className="focus-card stack-selection-modal context-edit-modal" role="dialog" aria-modal="true" aria-labelledby="context-edit-title">
        <header className="context-edit-modal-header">
          <h2 id="context-edit-title">{item.title || "Context item"}</h2>
          <button type="button" className="context-edit-close" aria-label="Close" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <form className="context-edit-form" onSubmit={handleSubmit}>
          <ContextEditFields
            context={context}
            draft={draft}
            firstFieldRef={firstFieldRef}
            item={item}
            updateDraft={updateDraft}
          />

          {error ? <p className="context-edit-message is-error">{error}</p> : null}

          <div className="context-edit-actions">
            <div>
              {canDelete ? (
                <button type="button" className="context-edit-delete" onClick={handleDelete} disabled={saveState !== "idle"}>
                  <Trash2 size={14} aria-hidden="true" />
                  <span>{saveState === "deleting" ? "Deleting" : "Delete"}</span>
                </button>
              ) : null}
            </div>
            <div className="context-edit-primary-actions">
              <button type="button" className="context-edit-cancel" onClick={onClose} disabled={saveState !== "idle"}>
                Cancel
              </button>
              <button type="submit" className="context-edit-save" disabled={saveState !== "idle"}>
                {saveState === "saving" ? "Saving" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

function ContextStoryboardFrameEditModal({ error, item, onClose, onSave, saveState }) {
  const firstFieldRef = useRef(null)
  const [draft, setDraft] = useState(() => draftFromStoryboardFrameItem(item))

  useEffect(() => {
    setDraft(draftFromStoryboardFrameItem(item))
    window.setTimeout(() => firstFieldRef.current?.focus(), 0)
  }, [item])

  useEffect(() => {
    if (!item) return undefined

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [item, onClose])

  if (!item) return null

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose()
  }

  function handleSubmit(event) {
    event.preventDefault()

    const formData = new FormData(event.currentTarget)
    onSave(payloadFromStoryboardFrameDraft({
      title: formData.get("title") ?? draft.title,
      description: formData.get("description") ?? draft.description,
      detailPrompt: formData.get("detailPrompt") ?? draft.detailPrompt
    }))
  }

  const modal = (
    <div className="stack-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div className="focus-card stack-selection-modal context-edit-modal context-storyboard-frame-edit-modal" role="dialog" aria-modal="true" aria-label="Edit frame text">
        <form className="context-edit-form" onSubmit={handleSubmit}>
          <ContextTextField inputRef={firstFieldRef} label="title" name="title" required value={draft.title} onChange={(value) => updateDraft("title", value)} />
          <ContextTextField label="display caption" name="description" required value={draft.description} onChange={(value) => updateDraft("description", value)} />
          <ContextTextField label="detail prompt" name="detailPrompt" required value={draft.detailPrompt} onChange={(value) => updateDraft("detailPrompt", value)} />

          {error ? <p className="context-edit-message is-error">{error}</p> : null}

          <div className="context-edit-actions">
            <div />
            <div className="context-edit-primary-actions">
              <button type="button" className="context-edit-cancel" onClick={onClose} disabled={saveState !== "idle"}>
                Cancel
              </button>
              <button type="submit" className="context-edit-save" disabled={saveState !== "idle"}>
                {saveState === "saving" ? "Saving" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

function ContextEditFields({ context, draft, firstFieldRef, item, updateDraft }) {
  if (item.section === "product_summary") {
    return (
      <>
        <ContextTextField inputRef={firstFieldRef} label="description" value={draft.description} onChange={(value) => updateDraft("description", value)} />
      </>
    )
  }

  if (item.section === "users") {
    return (
      <>
        <ContextTextField inputRef={firstFieldRef} label="name" required value={draft.title} onChange={(value) => updateDraft("title", value)} />
        <ContextTextField label="headline quote" value={draft.headlineQuote} onChange={(value) => updateDraft("headlineQuote", value)} />
        <ContextTextField label="role" value={draft.role} onChange={(value) => updateDraft("role", value)} />
        <ContextTextField label="description" value={draft.description} onChange={(value) => updateDraft("description", value)} />
        <ContextTextField label="narrative" value={draft.narrative} onChange={(value) => updateDraft("narrative", value)} />
        <ContextTextField label="how to spot" value={draft.howToSpot} onChange={(value) => updateDraft("howToSpot", value)} />
        <ContextTextField label="primary goal" value={draft.primaryGoal} onChange={(value) => updateDraft("primaryGoal", value)} />
        <ContextArrayField label="secondary goals" values={draft.secondaryGoals} onChange={(value) => updateDraft("secondaryGoals", value)} />
        <ContextArrayField label="care-abouts" values={draft.careAbouts} onChange={(value) => updateDraft("careAbouts", value)} />
        <ContextArrayField label="top issues" values={draft.topIssues} onChange={(value) => updateDraft("topIssues", value)} />
        <ContextArrayField label="pain points" values={draft.painPoints} onChange={(value) => updateDraft("painPoints", value)} />
        <ContextArrayField label="constraints" values={draft.constraints} onChange={(value) => updateDraft("constraints", value)} />
        <ContextArrayField label="decision criteria" values={draft.decisionCriteria} onChange={(value) => updateDraft("decisionCriteria", value)} />
      </>
    )
  }

  if (item.section === "actions") {
    return (
      <>
        <ContextTextField inputRef={firstFieldRef} label="label" required value={draft.title} onChange={(value) => updateDraft("title", value)} />
        <ContextTextField label="description" value={draft.description} onChange={(value) => updateDraft("description", value)} />
      </>
    )
  }

  if (item.section === "outcomes") {
    return (
      <>
        <ContextTextField inputRef={firstFieldRef} label="label" required value={draft.title} onChange={(value) => updateDraft("title", value)} />
        <ContextTextField label="description" value={draft.description} onChange={(value) => updateDraft("description", value)} />
        <ContextTextField label="impact" value={draft.impact} onChange={(value) => updateDraft("impact", value)} />
      </>
    )
  }

  if (item.section === "system_objects") {
    return (
      <>
        <ContextTextField inputRef={firstFieldRef} label="label" required value={draft.title} onChange={(value) => updateDraft("title", value)} />
        <ContextTextField label="kind" value={draft.kind} onChange={(value) => updateDraft("kind", value)} />
        <ContextTextField label="description" value={draft.description} onChange={(value) => updateDraft("description", value)} />
      </>
    )
  }

  return (
    <>
      <ContextRelationshipField
        context={context}
        draft={draft}
        firstFieldRef={firstFieldRef}
        updateDraft={updateDraft}
      />
      <ContextTextField label="label" value={draft.label} onChange={(value) => updateDraft("label", value)} />
    </>
  )
}

function ContextTextField({ inputRef, label, name, onChange, required = false, value }) {
  return (
    <label className="domain-field context-edit-field">
      <span>{label}</span>
      <AutoGrowTextarea
        inputRef={inputRef}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
      />
    </label>
  )
}

function ContextIconField({ label, onChange, value }) {
  const inputId = useId()
  const listboxId = useId()
  const ignoreNextInputFocusRef = useRef(false)
  const rootRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState(value || "")
  const normalizedQuery = normalizeIconSearch(query)
  const matchedOptions = useMemo(() => (
    commonIconOptions
      .filter((option) => iconOptionMatches(option, normalizedQuery))
  ), [normalizedQuery])
  const displayedOptions = matchedOptions.length ? matchedOptions : commonIconOptions

  useEffect(() => {
    setQuery(value || "")
  }, [value])

  function handleBlur(event) {
    const nextFocus = event.relatedTarget

    if (nextFocus && rootRef.current?.contains(nextFocus)) return

    setIsOpen(false)
  }

  function handleInputChange(event) {
    const nextValue = event.target.value
    setQuery(nextValue)
    onChange(nextValue)
    setIsOpen(true)
  }

  function handleInputFocus() {
    if (ignoreNextInputFocusRef.current) {
      ignoreNextInputFocusRef.current = false
      return
    }

    setIsOpen(true)
  }

  function focusInputWithoutOpening() {
    const input = document.getElementById(inputId)
    if (!input || document.activeElement === input) return

    ignoreNextInputFocusRef.current = true
    input.focus({ preventScroll: true })
  }

  function selectOption(option) {
    setQuery(option.key)
    onChange(option.key)
    setIsOpen(false)
    focusInputWithoutOpening()
  }

  function focusOption(index) {
    const option = displayedOptions[index]
    if (!option) return

    document.getElementById(iconOptionId(listboxId, option))?.focus()
  }

  function handleInputKeyDown(event) {
    if (event.key === "Escape") {
      setIsOpen(false)
      return
    }

    if (event.key !== "ArrowDown" || !displayedOptions.length) return

    event.preventDefault()
    setIsOpen(true)
    window.requestAnimationFrame(() => focusOption(0))
  }

  function handleOptionKeyDown(event, option, index) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      selectOption(option)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      setIsOpen(false)
      focusInputWithoutOpening()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      focusOption(Math.min(index + 1, displayedOptions.length - 1))
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      if (index === 0) {
        document.getElementById(inputId)?.focus()
      } else {
        focusOption(index - 1)
      }
    }
  }

  return (
    <div className="context-edit-field context-icon-field" ref={rootRef} onBlur={handleBlur}>
      <label htmlFor={inputId}>
        <span>{label}</span>
      </label>
      <div className="context-icon-search">
        <SearchIcon size={14} aria-hidden="true" />
        <input
          id={inputId}
          type="text"
          value={query}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder="Search icons"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen}
        />
      </div>
      {isOpen && (
        <div className="context-icon-options" id={listboxId} role="listbox" aria-label="Suggested icons">
          {matchedOptions.length ? null : (
            <p className="context-icon-empty">No exact match. Showing all icons alphabetically.</p>
          )}
          {displayedOptions.map((option, index) => {
            const Icon = option.icon || iconMap[option.key] || Sparkles
            const selected = option.key === value

            return (
              <div
                className={`context-icon-option ${selected ? "is-selected" : ""}`}
                id={iconOptionId(listboxId, option)}
                key={option.key}
                onClick={() => selectOption(option)}
                onKeyDown={(event) => handleOptionKeyDown(event, option, index)}
                onPointerDown={(event) => {
                  event.preventDefault()
                  selectOption(option)
                }}
                role="option"
                aria-selected={selected}
                tabIndex={-1}
              >
                <Icon size={15} aria-hidden="true" />
                <span>{option.label}</span>
                <small>{option.key}</small>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ContextArrayField({ label, onChange, values = [] }) {
  const draftValues = Array.isArray(values) ? values : [ values ]
  const items = draftValues.length ? draftValues.map((item) => String(item ?? "")) : [ "" ]

  function updateItem(index, value) {
    onChange(items.map((item, itemIndex) => itemIndex === index ? value : item))
  }

  function addItem() {
    onChange([ ...items, "" ])
  }

  function removeItem(index) {
    const nextItems = items.filter((_item, itemIndex) => itemIndex !== index)
    onChange(nextItems.length ? nextItems : [ "" ])
  }

  return (
    <fieldset className="context-array-field">
      <legend>{label}</legend>
      <div className="context-array-items">
        {items.map((item, index) => (
          <div className="context-array-item" key={index}>
            <AutoGrowTextarea
              ariaLabel={`${label} ${index + 1}`}
              value={item}
              onChange={(value) => updateItem(index, value)}
            />
            <button type="button" className="context-array-remove" aria-label={`Remove ${label} ${index + 1}`} onClick={() => removeItem(index)}>
              <X size={13} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="context-array-add" onClick={addItem}>
        <Plus size={13} aria-hidden="true" />
        <span>Add item</span>
      </button>
    </fieldset>
  )
}

function AutoGrowTextarea({ ariaLabel, inputRef, name, onChange, required = false, value }) {
  const textareaRef = useRef(null)

  useEffect(() => {
    resizeTextarea(textareaRef.current)
  }, [value])

  function setTextareaRef(node) {
    textareaRef.current = node
    if (inputRef) inputRef.current = node
    resizeTextarea(node)
  }

  function handleChange(event) {
    resizeTextarea(event.target)
    onChange(event.target.value)
  }

  return (
    <textarea
      ref={setTextareaRef}
      aria-label={ariaLabel}
      name={name}
      rows={1}
      value={value ?? ""}
      onChange={handleChange}
      required={required}
    />
  )
}

function ContextRelationshipField({ context, draft, firstFieldRef, updateDraft }) {
  const objects = relationshipObjects(context?.system?.objects)

  return (
    <div className="context-edit-field-grid">
      <label className="domain-field context-edit-field">
        <span>from</span>
        <select
          ref={firstFieldRef}
          value={draft.from}
          onChange={(event) => updateDraft("from", event.target.value)}
          required
        >
          {objects.map((object) => (
            <option key={object.id} value={object.id}>{object.label}</option>
          ))}
        </select>
      </label>
      <label className="domain-field context-edit-field">
        <span>to</span>
        <select
          value={draft.to}
          onChange={(event) => updateDraft("to", event.target.value)}
          required
        >
          {objects.map((object) => (
            <option key={object.id} value={object.id}>{object.label}</option>
          ))}
        </select>
      </label>
    </div>
  )
}

function draftFromContextItem(editor) {
  const item = editor?.item || {}

  switch (editor?.section) {
  case "product_summary":
    return {
      description: item.description || ""
    }
  case "users":
    return {
      careAbouts: item.careAbouts || item.care_abouts || [],
      constraints: item.constraints || [],
      decisionCriteria: item.decisionCriteria || item.decision_criteria || [],
      description: item.description || "",
      headlineQuote: item.headlineQuote || item.headline_quote || item.quote || "",
      howToSpot: item.howToSpot || item.how_to_spot || "",
      narrative: item.narrative || item.story || "",
      painPoints: item.painPoints || item.pain_points || item.criticalNeeds || item.critical_needs || [],
      primaryGoal: item.primaryGoal || item.primary_goal || "",
      role: item.role || "",
      secondaryGoals: item.secondaryGoals || item.secondary_goals || item.goals || [],
      topIssues: item.topIssues || item.top_issues || [],
      title: item.name || ""
    }
  case "actions":
    return {
      description: item.description || "",
      title: item.label || ""
    }
  case "outcomes":
    return {
      description: item.description || "",
      impact: item.impact || "",
      title: item.label || ""
    }
  case "system_objects":
    return {
      description: item.description || "",
      kind: item.kind || "",
      title: item.label || ""
    }
  case "system_relationships":
    return {
      from: item.from || "",
      label: item.label || "",
      to: item.to || ""
    }
  default:
    return {}
  }
}

function draftFromStoryboardFrameItem(editor) {
  const frame = editor?.frame || {}

  return {
    description: frame.description || "",
    detailPrompt: storyboardFrameDetailPrompt(frame),
    title: frame.title || ""
  }
}

function payloadFromStoryboardFrameDraft(draft) {
  return {
    description: String(draft.description ?? "").trim(),
    detail_prompt: String(draft.detailPrompt ?? "").trim(),
    title: String(draft.title ?? "").trim()
  }
}

function compactDraftArray(values = []) {
  return (Array.isArray(values) ? values : [ values ])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
}

function payloadFromContextDraft(editor, draft) {
  switch (editor.section) {
  case "product_summary":
    return {
      description: draft.description
    }
  case "users":
    return {
      name: draft.title,
      care_abouts: compactDraftArray(draft.careAbouts),
      constraints: compactDraftArray(draft.constraints),
      decision_criteria: compactDraftArray(draft.decisionCriteria),
      description: draft.description,
      headline_quote: draft.headlineQuote,
      how_to_spot: draft.howToSpot,
      narrative: draft.narrative,
      pain_points: compactDraftArray(draft.painPoints),
      position: editor.item?.position,
      primary_goal: draft.primaryGoal,
      role: draft.role,
      secondary_goals: compactDraftArray(draft.secondaryGoals),
      top_issues: compactDraftArray(draft.topIssues)
    }
  case "actions":
    return {
      label: draft.title,
      description: draft.description
    }
  case "outcomes":
    return {
      label: draft.title,
      description: draft.description,
      impact: draft.impact
    }
  case "system_objects":
    return {
      label: draft.title,
      kind: draft.kind,
      description: draft.description,
      position: editor.item?.position
    }
  case "system_relationships":
    return {
      from: draft.from,
      to: draft.to,
      label: draft.label
    }
  default:
    return {}
  }
}

function deletableContextItem(editor) {
  if (!editor || editor.creating) return false

  return [
    "users",
    "actions",
    "outcomes",
    "system_objects",
    "system_relationships"
  ].includes(editor.section)
}

function listToDraft(items = []) {
  return limitedList(items).map((item) => String(item ?? ""))
}

function parseDraftList(values = []) {
  return (Array.isArray(values) ? values : [ values ])
    .flat()
    .map((item) => String(item ?? "").trim())
    .filter(Boolean)
}

function resizeTextarea(textarea) {
  if (!textarea) return

  textarea.style.height = "auto"
  textarea.style.height = `${textarea.scrollHeight}px`
}

function contextFlowNodes(graph, canEdit) {
  return graph.nodes.map((node, index) => ({
    id: node.id,
    type: "contextObject",
    position: { x: node.x, y: node.y },
    data: {
      description: node.description,
      editable: canEdit,
      kind: node.kind,
      label: node.label,
      object: node,
      palette: nodePalette[index % nodePalette.length]
    }
  }))
}

function contextFlowEdges(graph, canEdit) {
  return graph.edges.map((edge) => {
    const displayLabel = relationshipDisplayLabel(edge)
    const handles = contextEdgeHandles(edge)

    return {
      id: `${relationshipKeyFor(edge)}-${edge.sourceIndex}`,
      source: edge.from.id,
      sourceHandle: handles.sourceHandle,
      target: edge.to.id,
      targetHandle: handles.targetHandle,
      type: "default",
      animated: false,
      data: { relationship: edge },
      focusable: canEdit,
      interactionWidth: 18,
      label: displayLabel || (canEdit ? "Unlabeled" : undefined),
      labelBgBorderRadius: 5,
      labelBgPadding: [ 6, 3 ],
      labelBgStyle: {
        fill: "rgba(12, 12, 13, 0.94)",
        stroke: "rgba(255, 255, 255, 0.09)"
      },
      labelShowBg: true,
      labelStyle: {
        fill: "rgba(255, 255, 255, 0.64)",
        fontSize: 10,
        fontWeight: 500
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "rgba(255, 255, 255, 0.32)",
        height: 14,
        width: 14
      },
      style: {
        stroke: "rgba(255, 255, 255, 0.28)",
        strokeWidth: 1.35
      }
    }
  })
}

export function contextEdgeHandles(edge = {}) {
  const from = contextNodeBounds(edge.from)
  const to = contextNodeBounds(edge.to)
  const horizontal = contextHorizontalEdgeGap(from, to)
  const vertical = contextVerticalEdgeGap(from, to)
  const useHorizontal = contextUseHorizontalEdgeRoute(from, to, horizontal, vertical)
  const sourceSide = useHorizontal ? horizontal.sourceSide : vertical.sourceSide
  const targetSide = useHorizontal ? horizontal.targetSide : vertical.targetSide

  return {
    sourceHandle: `source-${sourceSide}`,
    targetHandle: `target-${targetSide}`
  }
}

export function contextGraphWithFlowNodePositions(graph = {}, flowNodes = []) {
  const flowPositionById = new Map(
    (Array.isArray(flowNodes) ? flowNodes : []).map((node) => [node.id, graphPosition(node.position)])
      .filter((entry) => entry[0] && entry[1])
  )
  if (!flowPositionById.size) return graph

  const nodes = (Array.isArray(graph.nodes) ? graph.nodes : []).map((node) => {
    const position = flowPositionById.get(node.id)
    if (!position) return node

    return {
      ...node,
      x: position.x,
      y: position.y
    }
  })
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const edges = (Array.isArray(graph.edges) ? graph.edges : []).map((edge) => {
    const fromId = contextGraphEdgeEndpointId(edge, "from")
    const toId = contextGraphEdgeEndpointId(edge, "to")

    return {
      ...edge,
      from: nodeById.get(fromId) || edge.from,
      to: nodeById.get(toId) || edge.to
    }
  })

  return {
    ...graph,
    edges,
    nodes
  }
}

function contextGraphEdgeEndpointId(edge = {}, endpoint) {
  return edge[`${endpoint}Id`] || relationshipEndpointId(edge[endpoint])
}

function contextNodeBounds(node = {}) {
  const dimensions = contextNodeDimensions(node)
  const x = Number(node.x || 0)
  const y = Number(node.y || 0)
  const halfWidth = dimensions.width / 2
  const halfHeight = dimensions.height / 2

  return {
    bottom: y + halfHeight,
    height: dimensions.height,
    left: x - halfWidth,
    right: x + halfWidth,
    top: y - halfHeight,
    width: dimensions.width,
    x,
    y
  }
}

function contextNodeDimensions() {
  return { height: graphDefaults.nodeHeight, width: graphDefaults.nodeWidth }
}

function contextUseHorizontalEdgeRoute(from, to, horizontal, vertical) {
  if (horizontal.gap > 0 && vertical.gap === 0) return true
  if (vertical.gap > 0 && horizontal.gap === 0) return false
  if (horizontal.gap > 0 && vertical.gap > 0) return horizontal.gap <= vertical.gap

  return Math.abs(to.x - from.x) >= Math.abs(to.y - from.y)
}

function contextHorizontalEdgeGap(from, to) {
  if (from.right <= to.left) {
    return { gap: to.left - from.right, sourceSide: "right", targetSide: "left" }
  }

  if (to.right <= from.left) {
    return { gap: from.left - to.right, sourceSide: "left", targetSide: "right" }
  }

  return to.x >= from.x
    ? { gap: 0, sourceSide: "right", targetSide: "left" }
    : { gap: 0, sourceSide: "left", targetSide: "right" }
}

function contextVerticalEdgeGap(from, to) {
  if (from.bottom <= to.top) {
    return { gap: to.top - from.bottom, sourceSide: "bottom", targetSide: "top" }
  }

  if (to.bottom <= from.top) {
    return { gap: from.top - to.bottom, sourceSide: "top", targetSide: "bottom" }
  }

  return to.y >= from.y
    ? { gap: 0, sourceSide: "bottom", targetSide: "top" }
    : { gap: 0, sourceSide: "top", targetSide: "bottom" }
}

export function graphChangesWithoutLocalDeletes(changes = []) {
  return changes.filter((change) => change.type !== "remove")
}

function contextFlowEdgesEqual(leftEdges = [], rightEdges = []) {
  return contextFlowEdgesSignature(leftEdges) === contextFlowEdgesSignature(rightEdges)
}

function contextFlowEdgesSignature(edges = []) {
  return (Array.isArray(edges) ? edges : []).map((edge) => [
    edge.id,
    edge.source,
    edge.sourceHandle,
    edge.target,
    edge.targetHandle,
    edge.label || "",
    edge.focusable ? "focusable" : "",
    edge.markerEnd?.color || "",
    edge.style?.stroke || "",
    edge.style?.strokeWidth || ""
  ].join("\u001e")).join("\u001f")
}

export function contextGraphNodeSignature(graph = {}, canEdit = false) {
  return contextGraphCollectionSignature(graph.nodes, canEdit)
}

export function contextGraphEdgeSignature(graph = {}, canEdit = false) {
  return contextGraphCollectionSignature(graph.edges, canEdit)
}

function contextGraphCollectionSignature(items = [], canEdit = false) {
  return [
    canEdit ? "editable" : "readonly",
    ...(Array.isArray(items) ? items : []).map(contextGraphSerializedValue)
  ].join("\u001f")
}

function contextGraphSerializedValue(value) {
  try {
    return JSON.stringify(contextGraphPositionlessValue(value))
  } catch {
    return String(value)
  }
}

function contextGraphPositionlessValue(value) {
  if (Array.isArray(value)) return value.map(contextGraphPositionlessValue)
  if (!value || typeof value !== "object") return value

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !contextSemanticSignatureIgnoredKey(key))
      .map(([key, nestedValue]) => [key, contextGraphPositionlessValue(nestedValue)])
  )
}

function preventLocalGraphDeletion() {
  return false
}

function roundedFlowPosition(position) {
  return {
    x: Math.round(position.x),
    y: Math.round(position.y)
  }
}

function systemObjectPositionAttributes(node, position) {
  return {
    description: node.description || "",
    id: node.id,
    kind: node.kind || "",
    label: node.label,
    position
  }
}

export function contextWithSystemObjectPosition(context, objectId, position) {
  if (!context || !objectId || !position) return context

  const objects = limitedList(context.system?.objects)
  if (!objects.some((object) => object.id === objectId)) return context

  return {
    ...context,
    system: {
      ...(context.system || {}),
      objects: objects.map((object) => object.id === objectId ? { ...object, position } : object)
    }
  }
}

const diagramTypeOrder = [ "workflow", "dataflow", "architecture", "sequence", "lifecycle" ]
const DIAGRAM_STREAM_EVENT_LIMIT = 200

export function diagramsByActionKey(diagrams = []) {
  return diagramList(diagrams).reduce((groups, diagram) => {
    if (!diagram?.actionKey) return groups

    const current = groups.get(diagram.actionKey) || []
    current.push(diagram)
    groups.set(diagram.actionKey, current)
    return groups
  }, new Map())
}

function readyActionDiagrams(diagrams = []) {
  return diagramList(diagrams)
    .filter((diagram) => diagram?.status === "ready" && (diagram.embedUrl || diagram.standaloneUrl))
    .sort((left, right) => diagramTypeRank(left) - diagramTypeRank(right))
}

export function diagramList(diagrams = []) {
  return Array.isArray(diagrams) ? diagrams : []
}

export function emptyActionDiagramGenerationKey({
  action,
  busy = false,
  canGenerate = false,
  error = null,
  failedDiagram = null,
  generatingDiagram = null,
  selectedDiagram = null
} = {}) {
  if (!canGenerate || busy || selectedDiagram || generatingDiagram || failedDiagram || error) return null
  if (!action || (!action.key && !action.label && !action.name)) return null

  const actionKey = contextActionKey(action)
  if (!actionKey) return null

  return `${actionKey}:workflow`
}

export function requestActionDiagramGeneration(onGenerate, diagram) {
  onGenerate?.({ diagramType: diagram?.diagramType })
}

function diagramStreamKey(diagram) {
  if (!diagram) return "none"

  return [
    diagram.id || diagram.actionKey,
    diagram.diagramType || "workflow",
    diagram.status || "idle",
    diagram.eventsUrl || ""
  ].join(":")
}

function mergeDiagramStreamEvents(currentEvents = [], incomingEvents = []) {
  const eventsBySequence = new Map()

  ;[...currentEvents, ...incomingEvents].forEach((event) => {
    const normalized = normalizeDiagramStreamEvent(event)
    if (!normalized) return

    const sequence = Number(normalized.sequence)
    if (!Number.isFinite(sequence) || sequence <= 0) return

    eventsBySequence.set(sequence, {
      ...normalized,
      sequence
    })
  })

  return [...eventsBySequence.values()]
    .sort((a, b) => a.sequence - b.sequence)
    .slice(-DIAGRAM_STREAM_EVENT_LIMIT)
}

function diagramFailureMessage(diagram, error, streamEvents = []) {
  const explicitMessage = String(diagram?.error || error || "").trim()
  if (explicitMessage) return explicitMessage

  const retainedError = [...streamEvents].reverse().find((event) => event?.type === "error")
  return String(retainedError?.text || "Diagram generation failed.").trim() || "Diagram generation failed."
}

function diagramStreamEventsWithFailure(streamEvents = [], failureMessage) {
  const message = String(failureMessage || "").trim()
  if (!message || streamEvents.some((event) => event?.text === message)) return streamEvents

  return mergeDiagramStreamEvents(streamEvents, [{
    sequence: lastDiagramStreamSequence(streamEvents) + 1,
    type: "error",
    text: message
  }])
}

function normalizeDiagramStreamEvent(event) {
  if (!event || event.type === "heartbeat") return null

  const activity = event.activity || null
  const text = formatEventText(event.text || event.raw_type || event.rawType || "")
  if (!text && !activity) return null

  return {
    activity,
    key: `${event.sequence || text || activity?.label}-${event.type || "event"}`,
    rawType: event.raw_type || event.rawType,
    sequence: event.sequence,
    text,
    type: event.type || "status"
  }
}

function lastDiagramStreamSequence(events = [], fallback = 0) {
  return events.reduce((sequence, event) => {
    return Math.max(sequence, Number(event?.sequence) || 0)
  }, Number(fallback) || 0)
}

function diagramTypeRank(diagram) {
  const index = diagramTypeOrder.indexOf(diagram?.diagramType)
  return index === -1 ? diagramTypeOrder.length : index
}

function diagramTypeLabel(diagram = {}) {
  if (diagram.label) return diagram.label

  return {
    architecture: "Architecture",
    dataflow: "Data flow",
    lifecycle: "Lifecycle",
    sequence: "Sequence",
    workflow: "Workflow"
  }[diagram.diagramType] || "Diagram"
}

function journeyModelItems(action = {}) {
  return codeEnablerItems(action.codeEnablers?.dataModels)
}

function journeyWorkflowItems(action = {}) {
  return codeEnablerItems(action.codeEnablers?.workflowCode || action.codeEnablers?.services)
}

function codeEnablerItems(items = []) {
  return limitedList(items).map((item) => ({
    label: item.name || item.label || item.path,
    path: item.path,
    pattern: item.pattern || item.kind || item.type,
    role: item.role || item.description
  })).filter((item) => item.label && item.path)
}

export function relationshipGraph(system = {}) {
  const nodes = relationshipObjects(system.objects)
  const byId = new Map(nodes.map((node) => [node.id, node]))
  const relationships = limitedList(system.relationships)
    .map((relationship, index) => ({ ...relationship, sourceIndex: index }))
    .filter((relationship) => (
      byId.has(relationship.from) && byId.has(relationship.to)
    ))
  const incoming = new Map(nodes.map((node) => [node.id, 0]))

  relationships.forEach((relationship) => {
    incoming.set(relationship.to, (incoming.get(relationship.to) || 0) + 1)
  })

  const roots = nodes.filter((node) => incoming.get(node.id) === 0)
  const rootIds = roots.length ? roots.map((node) => node.id) : [nodes[0]?.id].filter(Boolean)
  const columnById = new Map(nodes.map((node) => [node.id, 0]))
  rootIds.forEach((id) => columnById.set(id, 0))

  for (let pass = 0; pass < nodes.length; pass += 1) {
    relationships.forEach((relationship) => {
      const fromColumn = columnById.get(relationship.from) || 0
      const toColumn = columnById.get(relationship.to) || 0
      const nextColumn = Math.min(fromColumn + 1, nodes.length - 1)
      if (nextColumn > toColumn) columnById.set(relationship.to, nextColumn)
    })
  }

  const columns = [...new Set(nodes.map((node) => columnById.get(node.id) || 0))].sort((a, b) => a - b)
  const nodesByColumn = new Map()

  nodes.forEach((node) => {
    const column = columnById.get(node.id) || 0
    const list = nodesByColumn.get(column) || []
    list.push(node)
    nodesByColumn.set(column, list)
  })

  const positioned = shouldUseGridLayout({ columns, nodes, nodesByColumn, relationships })
    ? gridPositionedGraph(nodes)
    : (shouldUseWrappedLayeredLayout({ columns, relationships })
        ? wrappedLayeredPositionedGraph({ columns, columnById, nodes, nodesByColumn })
        : layeredPositionedGraph({ columns, columnById, nodes, nodesByColumn }))
  const positionedWithOverrides = contextGraphWithReadableSavedPositions(positioned, applySavedGraphPositions)
  const positionedNodes = positionedWithOverrides.nodes
  const positionedById = new Map(positionedNodes.map((node) => [node.id, node]))

  return {
    height: positionedWithOverrides.height,
    width: positionedWithOverrides.width,
    nodes: positionedNodes,
    edges: relationships.map((relationship) => ({
      ...relationship,
      from: positionedById.get(relationship.from),
      to: positionedById.get(relationship.to)
    })).filter((edge) => edge.from && edge.to)
  }
}

function applySavedGraphPositions(graph) {
  const nodes = graph.nodes.map((node) => {
    const position = graphPosition(node.position)
    if (!position) return node

    return { ...node, x: position.x, y: position.y }
  })

  return { ...graph, nodes }
}

function contextGraphWithReadableSavedPositions(graph, applySavedPositions) {
  const generatedGraph = normalizeContextGraphLayout(graph)
  if (!contextGraphHasSavedPositions(graph.nodes)) return generatedGraph

  const savedGraph = normalizeContextGraphLayout(applySavedPositions(graph))
  return contextGraphLayoutReadable(savedGraph) ? savedGraph : generatedGraph
}

function contextGraphHasSavedPositions(nodes = []) {
  return (Array.isArray(nodes) ? nodes : []).some((node) => graphPosition(node.position))
}

function contextGraphLayoutReadable(graph = {}) {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : []
  if (!nodes.length) return true

  return contextGraphNodesInsideBounds(graph) && !contextGraphNodesOverlap(nodes, contextGraphReadableGap)
}

function contextGraphNodesInsideBounds(graph = {}) {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : []
  if (!nodes.length) return true

  const width = Number(graph.width) || graphDefaults.width
  const height = Number(graph.height) || graphDefaults.height
  const bounds = contextGraphNodeBounds(nodes)

  return bounds.left >= contextGraphLayoutPadding - 0.5 &&
    bounds.top >= contextGraphLayoutPadding - 0.5 &&
    bounds.right <= width - contextGraphLayoutPadding + 0.5 &&
    bounds.bottom <= height - contextGraphLayoutPadding + 0.5
}

function contextGraphNodesOverlap(nodes = [], gap = 0) {
  const paddedGap = Math.max(0, gap / 2)

  for (let index = 0; index < nodes.length; index += 1) {
    const nodeBounds = contextPaddedNodeBounds(nodes[index], paddedGap)

    for (let otherIndex = index + 1; otherIndex < nodes.length; otherIndex += 1) {
      if (contextBoundsOverlap(nodeBounds, contextPaddedNodeBounds(nodes[otherIndex], paddedGap))) {
        return true
      }
    }
  }

  return false
}

function contextPaddedNodeBounds(node, padding = 0) {
  const bounds = contextNodeBounds(node)

  return {
    ...bounds,
    bottom: bounds.bottom + padding,
    left: bounds.left - padding,
    right: bounds.right + padding,
    top: bounds.top - padding
  }
}

function contextBoundsOverlap(leftBounds, rightBounds) {
  return leftBounds.left < rightBounds.right &&
    leftBounds.right > rightBounds.left &&
    leftBounds.top < rightBounds.bottom &&
    leftBounds.bottom > rightBounds.top
}

function normalizeContextGraphLayout(graph) {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : []
  if (!nodes.length) return graph

  const width = Math.max(Number(graph.width) || graphDefaults.width, widestContextGraphNode(nodes) + (contextGraphLayoutPadding * 2))
  const height = Math.max(Number(graph.height) || graphDefaults.height, tallestContextGraphNode(nodes) + (contextGraphLayoutPadding * 2))
  const bounds = contextGraphNodeBounds(nodes)
  const scale = contextGraphContainmentScale(bounds, width, height)
  const scaledNodes = scale < 1 ? scaleContextGraphNodes(nodes, bounds, scale) : nodes
  const scaledBounds = contextGraphNodeBounds(scaledNodes)
  const dx = contextGraphContainmentOffset(scaledBounds.left, scaledBounds.right, width)
  const dy = contextGraphContainmentOffset(scaledBounds.top, scaledBounds.bottom, height)
  if (scale === 1 && dx === 0 && dy === 0) return { ...graph, height, nodes, width }

  return {
    ...graph,
    height,
    width,
    nodes: scaledNodes.map((node) => ({
      ...node,
      x: Math.round(node.x + dx),
      y: Math.round(node.y + dy)
    }))
  }
}

function contextGraphNodeBounds(nodes = []) {
  return nodes.reduce((bounds, node) => {
    const nodeBounds = contextNodeBounds(node)

    return {
      bottom: Math.max(bounds.bottom, nodeBounds.bottom),
      left: Math.min(bounds.left, nodeBounds.left),
      maxCenterX: Math.max(bounds.maxCenterX, nodeBounds.x),
      maxCenterY: Math.max(bounds.maxCenterY, nodeBounds.y),
      minCenterX: Math.min(bounds.minCenterX, nodeBounds.x),
      minCenterY: Math.min(bounds.minCenterY, nodeBounds.y),
      right: Math.max(bounds.right, nodeBounds.right),
      top: Math.min(bounds.top, nodeBounds.top)
    }
  }, {
    bottom: -Infinity,
    left: Infinity,
    maxCenterX: -Infinity,
    maxCenterY: -Infinity,
    minCenterX: Infinity,
    minCenterY: Infinity,
    right: -Infinity,
    top: Infinity
  })
}

function contextGraphContainmentScale(bounds, width, height) {
  const availableCenterWidth = Math.max(0, width - (contextGraphLayoutPadding * 2) - (bounds.right - bounds.left - (bounds.maxCenterX - bounds.minCenterX)))
  const availableCenterHeight = Math.max(0, height - (contextGraphLayoutPadding * 2) - (bounds.bottom - bounds.top - (bounds.maxCenterY - bounds.minCenterY)))
  const centerWidth = bounds.maxCenterX - bounds.minCenterX
  const centerHeight = bounds.maxCenterY - bounds.minCenterY
  const scaleX = centerWidth > availableCenterWidth && centerWidth > 0 ? availableCenterWidth / centerWidth : 1
  const scaleY = centerHeight > availableCenterHeight && centerHeight > 0 ? availableCenterHeight / centerHeight : 1

  return Math.max(0, Math.min(1, scaleX, scaleY))
}

function scaleContextGraphNodes(nodes, bounds, scale) {
  const originX = (bounds.left + bounds.right) / 2
  const originY = (bounds.top + bounds.bottom) / 2

  return nodes.map((node) => ({
    ...node,
    x: originX + ((node.x - originX) * scale),
    y: originY + ((node.y - originY) * scale)
  }))
}

function contextGraphContainmentOffset(min, max, size) {
  const innerMin = contextGraphLayoutPadding
  const innerMax = size - contextGraphLayoutPadding
  if (max - min > innerMax - innerMin) return (size / 2) - ((min + max) / 2)
  if (min < innerMin) return innerMin - min
  if (max > innerMax) return innerMax - max

  return 0
}

function widestContextGraphNode(nodes = []) {
  return nodes.reduce((width, node) => Math.max(width, contextNodeDimensions(node).width), 0)
}

function tallestContextGraphNode(nodes = []) {
  return nodes.reduce((height, node) => Math.max(height, contextNodeDimensions(node).height), 0)
}

function graphPosition(position) {
  if (!position || typeof position !== "object") return null

  const x = Number(position.x)
  const y = Number(position.y)
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null

  return { x, y }
}

function shouldUseGridLayout({ columns, nodes, nodesByColumn, relationships }) {
  if (relationships.length === 0) return true
  if (columns.length <= 1 && nodes.length > 1) return true

  const maxColumnSize = Math.max(...[...nodesByColumn.values()].map((columnNodes) => columnNodes.length))
  return columns.length <= 2 && maxColumnSize > 3
}

function shouldUseWrappedLayeredLayout({ columns, relationships }) {
  return relationships.length > 0 && columns.length > 4
}

function gridPositionedGraph(nodes) {
  const columnCount = Math.min(4, Math.ceil(Math.sqrt(nodes.length || 1)))
  const rowCount = Math.ceil((nodes.length || 1) / columnCount)
  const width = Math.max(graphDefaults.width, columnCount * graphDefaults.minColumnGap + 110)
  const height = Math.max(graphDefaults.height, rowCount * graphDefaults.minRowGap + 100)
  const fullRowWidth = (columnCount - 1) * graphDefaults.minColumnGap
  const xStart = (width - fullRowWidth) / 2
  const yStart = (height - ((rowCount - 1) * graphDefaults.minRowGap)) / 2

  return {
    height,
    width,
    nodes: nodes.map((node, index) => {
      const row = Math.floor(index / columnCount)
      const column = index % columnCount
      const rowStart = row * columnCount
      const itemsInRow = Math.min(columnCount, nodes.length - rowStart)
      const rowOffset = ((columnCount - itemsInRow) * graphDefaults.minColumnGap) / 2

      return {
        ...node,
        x: xStart + rowOffset + (column * graphDefaults.minColumnGap),
        y: yStart + (row * graphDefaults.minRowGap)
      }
    })
  }
}

function layeredPositionedGraph({ columns, columnById, nodes, nodesByColumn }) {
  const columnIndex = new Map(columns.map((column, index) => [column, index]))
  const width = Math.max(graphDefaults.width, columns.length * graphDefaults.minColumnGap + 110)
  const maxColumnSize = Math.max(...[...nodesByColumn.values()].map((columnNodes) => columnNodes.length))
  const height = Math.max(graphDefaults.height, maxColumnSize * graphDefaults.minRowGap + 100)
  const xStart = columns.length <= 1
    ? width / 2
    : (width - ((columns.length - 1) * graphDefaults.minColumnGap)) / 2

  return {
    height,
    width,
    nodes: nodes.map((node) => {
      const column = columnById.get(node.id) || 0
      const peers = nodesByColumn.get(column) || [node]
      const peerIndex = peers.findIndex((peer) => peer.id === node.id)
      const yStart = peers.length <= 1
        ? height / 2
        : (height - ((peers.length - 1) * graphDefaults.minRowGap)) / 2

      return {
        ...node,
        x: columns.length <= 1 ? xStart : xStart + ((columnIndex.get(column) || 0) * graphDefaults.minColumnGap),
        y: yStart + (peerIndex * graphDefaults.minRowGap)
      }
    })
  }
}

function wrappedLayeredPositionedGraph({ columns, columnById, nodes, nodesByColumn }) {
  const columnIndex = new Map(columns.map((column, index) => [column, index]))
  const columnsPerRow = 4
  const rowGap = 240
  const slotGap = (graphDefaults.width - (2 * (contextGraphLayoutPadding + (graphDefaults.nodeWidth / 2)))) / (columnsPerRow - 1)
  const rowCount = Math.ceil(columns.length / columnsPerRow)
  const rowHalfSpreads = Array.from({ length: rowCount }, (_row, rowIndex) => {
    const rowColumns = columns.slice(rowIndex * columnsPerRow, (rowIndex + 1) * columnsPerRow)

    return rowColumns.reduce((spread, column) => {
      const peerCount = (nodesByColumn.get(column) || []).length
      return Math.max(spread, ((peerCount - 1) * graphDefaults.minRowGap) / 2)
    }, 0)
  })
  const rowCenters = contextWrappedLayerRowCenters(rowHalfSpreads, rowGap)
  const height = Math.max(
    graphDefaults.height,
    rowCenters[rowCenters.length - 1] + rowHalfSpreads[rowHalfSpreads.length - 1] + (graphDefaults.nodeHeight / 2) + contextGraphLayoutPadding
  )

  return {
    height,
    width: graphDefaults.width,
    nodes: nodes.map((node) => {
      const column = columnById.get(node.id) || 0
      const index = columnIndex.get(column) || 0
      const row = Math.floor(index / columnsPerRow)
      const slot = index % columnsPerRow
      const visualSlot = row % 2 === 0 ? slot : columnsPerRow - 1 - slot
      const peers = nodesByColumn.get(column) || [node]
      const peerIndex = peers.findIndex((peer) => peer.id === node.id)
      const yStart = rowCenters[row] - (((peers.length - 1) * graphDefaults.minRowGap) / 2)

      return {
        ...node,
        x: contextGraphLayoutPadding + (graphDefaults.nodeWidth / 2) + (visualSlot * slotGap),
        y: yStart + (peerIndex * graphDefaults.minRowGap)
      }
    })
  }
}

function contextWrappedLayerRowCenters(rowHalfSpreads = [], rowGap) {
  const centers = []

  rowHalfSpreads.forEach((spread, index) => {
    if (index === 0) {
      centers.push(contextGraphLayoutPadding + (graphDefaults.nodeHeight / 2) + Math.max(spread, 62))
      return
    }

    const previousSpread = rowHalfSpreads[index - 1] || 0
    const minimumGap = previousSpread + spread + graphDefaults.nodeHeight + contextGraphReadableGap
    centers.push(centers[index - 1] + Math.max(rowGap, minimumGap))
  })

  return centers
}

function limitedList(value) {
  return Array.isArray(value) ? value.filter(Boolean).slice(0, 7) : []
}

function contextHasGeneratingAvatars(context) {
  return limitedList(context?.users).some((user) => user?.avatar?.status === "generating")
}

export function contextGeneratingAvatarSignature(context) {
  const generatingUsers = limitedList(context?.users)
    .map((user, index) => generatingAvatarSignaturePart(user, index))
    .filter(Boolean)

  return generatingUsers.length ? generatingUsers.join("|") : null
}

function generatingAvatarSignaturePart(user, index) {
  if (user?.avatar?.status !== "generating") return null

  return `${user?.key || user?.name || index}:generating`
}

export function contextAvatarPollDelay(attempt = 0) {
  const normalizedAttempt = Number.isFinite(Number(attempt)) ? Math.max(0, Math.trunc(Number(attempt))) : 0

  return contextAvatarPollDelays[Math.min(normalizedAttempt, contextAvatarPollDelays.length - 1)]
}

function preloadContextAvatarImages(context) {
  const urls = limitedList(context?.users)
    .map((user) => user?.avatar?.url)
    .filter(Boolean)

  if (!urls.length) return Promise.resolve()

  return Promise.all(urls.map(preloadContextAvatarImage))
}

function preloadContextAvatarImage(url) {
  if (typeof window === "undefined" || typeof window.Image !== "function") return Promise.resolve()

  return new Promise((resolve) => {
    const image = new window.Image()
    const finish = () => resolve()

    image.onload = finish
    image.onerror = finish
    image.decoding = "sync"
    image.src = url

    if (image.complete) resolve()
  })
}

function personaToneFor(user = {}, index = 0) {
  const tones = [
    {
      key: "sage-coral",
      accent: "#ef8f78",
      background: "#dfe6d4"
    },
    {
      key: "sky-graphite",
      accent: "#82b9d7",
      background: "#dde8ec"
    },
    {
      key: "moss-gold",
      accent: "#dca75e",
      background: "#e4e1cb"
    },
    {
      key: "clay-green",
      accent: "#69b692",
      background: "#ead9ce"
    }
  ]
  const seed = personaSeed(personaDisplayName(user) || user.name || user.key || "", index * 3)

  return tones[seed % tones.length]
}

function personaSeed(value = "", fallback = 0) {
  return Array.from(value).reduce((sum, character) => sum + character.charCodeAt(0), fallback)
}

function personaInitials(value = "") {
  const words = value.toString().trim().split(/\s+/).filter(Boolean)
  const initials = words.slice(0, 2).map((word) => word[0]).join("")

  return initials.toUpperCase() || "?"
}

function compareIconOptions(left, right) {
  return left.label.localeCompare(right.label, undefined, { sensitivity: "base" }) ||
    left.key.localeCompare(right.key, undefined, { sensitivity: "base" })
}

function iconFor(value) {
  const normalized = normalizeIconSearch(value)
  const compact = normalized.replace(/\s+/g, "")
  const key = Object.keys(iconMap)
    .sort((left, right) => right.length - left.length)
    .find((candidate) => {
      const normalizedCandidate = normalizeIconSearch(candidate)
      return normalized.includes(normalizedCandidate) || compact.includes(normalizedCandidate.replace(/\s+/g, ""))
    })

  return iconMap[key] || Sparkles
}

function iconOptionMatches(option, query) {
  if (!query) return true

  const terms = query.split(/\s+/).filter(Boolean)
  const searchable = normalizeIconSearch([
    option.key,
    option.label,
    ...(option.keywords || [])
  ].join(" "))

  return terms.every((term) => searchable.includes(term))
}

function normalizeIconSearch(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
}

function iconOptionId(listboxId, option) {
  return `${listboxId}-${option.key}`
}

function objectIcon(kind) {
  const Icon = iconFor(kind || "service")
  return <Icon strokeWidth={1.7} />
}

function objectKindLabel(kind) {
  const text = String(kind || "").trim()
  if (!text) return ""

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
}

function relationshipKeyFor(relationship) {
  if (relationship.key) return relationship.key

  const from = relationshipEndpointId(relationship.from)
  const to = relationshipEndpointId(relationship.to)

  return `${from || ""}->${to || ""}`
}

function systemObjectEditorPayload(object) {
  return {
    section: "system_objects",
    key: object.id,
    title: object.label,
    item: object
  }
}

function systemRelationshipEditorPayload(relationship) {
  return {
    section: "system_relationships",
    key: relationshipKeyFor(relationship),
    title: relationshipTitleFor(relationship),
    item: {
      from: relationshipEndpointId(relationship.from),
      label: relationship.label ?? "",
      to: relationshipEndpointId(relationship.to)
    }
  }
}

function relationshipEndpointId(node) {
  if (!node) return ""
  if (typeof node === "string") return node

  return node.id || node.key || ""
}

function relationshipObjects(objects = []) {
  return limitedList(objects).map((object, index) => ({
    ...object,
    id: object.id || object.key || `object-${index}`,
    label: object.label || object.name || object.id || object.key || `Object ${index + 1}`,
    position: graphPosition(object.position),
    sourceIndex: index
  }))
}

function relationshipDisplayLabel(relationship) {
  return String(relationship.label ?? "").trim()
}

function relationshipTitleFor(relationship) {
  const label = relationshipDisplayLabel(relationship)
  if (label) return label

  const from = relationshipNodeLabel(relationship.from)
  const to = relationshipNodeLabel(relationship.to)
  if (from && to) return `${from} to ${to}`

  return "relationship"
}

function relationshipNodeLabel(node) {
  if (!node) return ""
  if (typeof node === "string") return node

  return node.label || node.name || node.id || node.key || ""
}

function contextLastSyncedLabel(value) {
  if (!value) return "Not synced yet"

  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return "Last synced recently"

  return `Last synced ${new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short"
  }).format(date)}`
}
